import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef, useCallback, useEffect } from "react";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import moment from "moment";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import ConfigHorarioSemanalRow from "./ConfigHorarioSemanalRow.tsx";
import BossProfileAvatar from "../../../direct-boss/BossProfileAvatar.tsx";
import { prefetchPartnerCurps } from "../../../../services/profileImageCache.ts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  normalizePositionIdKey,
  dedupeWeeklyTableItems,
  dedupeBranchSchedulesForDisplay,
  splitWeeklyCellParts,
  weeklyCellPartMatchesScheduleName,
  appendWeeklyCellPart,
  replaceWeeklyCellPartsForShift,
  replaceOneWeeklyCellPart,
  countWeeklyCellPartsForShift,
} from "./scheduleKeyUtils.ts";
import "./configHorarioSemanalTable.css";

function normalizeShiftName(shift: any): string {
  if (shift == null || shift === "") return "";
  if (typeof shift === "string") return shift.trim();
  if (typeof shift === "object" && (shift as { name?: string }).name != null) {
    return String((shift as { name: string }).name).trim();
  }
  return String(shift).trim();
}

const SCHEDULE_VIEW_SHIFT_ORDER = ["Matutino", "Intermedio", "Vespertino"] as const;
const ADD_SLOT_HOVER_DELAY_MS = 200;

/** Si la API aporta branchScheduleId para un slot, no enviar también el item duplicado solo desde la celda. */
function preferItemsWithBranchScheduleId(items: any[]): any[] {
  const sk = (s: unknown) => String(s ?? "").trim().toLowerCase();
  const withSidKeys = new Set(
    items
      .filter((it) => it.branchScheduleId != null)
      .map(
        (it) =>
          `${it.partnerId}|${it.day}|${sk(it.shift)}|${normalizePositionIdKey(it.positionId)}`,
      ),
  );
  return items.filter((it) => {
    if (it.branchScheduleId != null) return true;
    const k = `${it.partnerId}|${it.day}|${sk(it.shift)}|${normalizePositionIdKey(it.positionId)}`;
    if (withSidKeys.has(k)) return false;
    return true;
  });
}

const SHIFT_OPTION_MARK: Record<string, string> = {
  Matutino: "\uE000",
  Intermedio: "\uE001",
  Vespertino: "\uE002",
};

function parseWeeklyCellDropdownOption(opt: string): {
  display: string;
  explicitShift: string | null;
} {
  const s = String(opt ?? "").trim();
  const m = s.match(/^(.+?) \((Matutino|Intermedio|Vespertino)\)$/);
  if (m) return { display: m[1].trim(), explicitShift: m[2] };
  const last = s.length > 0 ? s[s.length - 1] : "";
  if (last === "\uE000") return { display: s.slice(0, -1).trim(), explicitShift: "Matutino" };
  if (last === "\uE001") return { display: s.slice(0, -1).trim(), explicitShift: "Intermedio" };
  if (last === "\uE002") return { display: s.slice(0, -1).trim(), explicitShift: "Vespertino" };
  return { display: s, explicitShift: null };
}

function normalizePositionLabel(value: string): string {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizePositionOrderKey(value: string): string {
  const raw = String(value ?? "")
    .replace(/\bsuc\b/gi, "")
    .replace(/\s+\d+$/, "")
    .trim();
  return normalizePositionLabel(raw);
}

function resolveShiftForSlotFilterOption(
  display: string,
  explicitShift: string | null,
  rowShift: string,
  positionGroupByShift: Record<
    string,
    { displayNameToPositionName: Record<string, string> }
  >,
  shiftOrder: readonly string[],
): string | null {
  if (explicitShift) return explicitShift;
  const displayNorm = normalizePositionLabel(display);
  const hasDisplayInShift = (shift: string) => {
    const direct = positionGroupByShift[shift]?.displayNameToPositionName?.[display];
    if (direct) return true;
    const keys = Object.keys(
      positionGroupByShift[shift]?.displayNameToPositionName || {},
    );
    return keys.some((k) => normalizePositionLabel(k) === displayNorm);
  };
  if (hasDisplayInShift(rowShift)) return rowShift;
  for (const sh of shiftOrder) {
    if (sh === rowShift) continue;
    if (hasDisplayInShift(sh)) return sh;
  }
  return null;
}

function orderCellDropdownOptionsByRowShiftFirst(
  options: string[],
  rowShift: string,
  positionGroupByShift: Record<
    string,
    { displayNameToPositionName: Record<string, string> }
  >,
): string[] {
  const descanso = options.includes("DESCANSO") ? ["DESCANSO"] : [];
  const nonDesc = options.filter((o) => o !== "DESCANSO");

  const blockOrder = [
    rowShift,
    ...SCHEDULE_VIEW_SHIFT_ORDER.filter((sh) => sh !== rowShift),
  ];

  const buckets: Record<string, string[]> = {};
  blockOrder.forEach((sh) => {
    buckets[sh] = [];
  });
  const unknown: string[] = [];

  nonDesc.forEach((opt) => {
    const { display, explicitShift } = parseWeeklyCellDropdownOption(opt);
    const optShift = resolveShiftForSlotFilterOption(
      display,
      explicitShift,
      rowShift,
      positionGroupByShift,
      SCHEDULE_VIEW_SHIFT_ORDER,
    );
    if (optShift && buckets[optShift]) buckets[optShift].push(opt);
    else unknown.push(opt);
  });

  blockOrder.forEach((sh) => {
    buckets[sh] = (buckets[sh] || []).sort((a, b) => a.localeCompare(b, "es"));
  });
  unknown.sort((a, b) => a.localeCompare(b, "es"));

  return [...descanso, ...blockOrder.flatMap((sh) => buckets[sh] || []), ...unknown];
}

function findPositionRecordForCellValue(
  value: string,
  candidateShift: string,
  positions: any[],
): any | null {
  const { display, explicitShift } = parseWeeklyCellDropdownOption(value);
  const norm = (sh: string) => normalizeShiftName(sh).toLowerCase();
  const cand = norm(candidateShift);
  const matchOne = (p: any) => {
    const dn = (p.positionDisplayName || "").trim();
    const pn = (p.positionName || "").trim();
    const displayNorm = normalizePositionLabel(display);
    return (
      normalizePositionLabel(dn) === displayNorm ||
      normalizePositionLabel(pn) === displayNorm ||
      normalizePositionLabel(dn).startsWith(displayNorm + " ") ||
      normalizePositionLabel(pn).startsWith(displayNorm + " ")
    );
  };
  if (explicitShift) {
    const es = norm(explicitShift);
    return positions.find((p: any) => norm(p.shift) === es && matchOne(p)) || null;
  }
  const matches = positions.filter(matchOne);
  if (matches.length === 0) return null;
  return matches.find((p: any) => norm(p.shift) === cand) || matches[0];
}

function resolvePositionAssignmentForPartnerDay(
  part: string,
  partner: any,
  day: string,
  positions: any[],
  getSchedulesForPartnerDay: (pid: number, d: string, sh?: string) => any[],
): any | null {
  const { display, explicitShift } = parseWeeklyCellDropdownOption(part);
  const norm = (sh: string) => normalizeShiftName(sh).toLowerCase();
  const matchOne = (p: any) => {
    const dn = (p.positionDisplayName || "").trim();
    const pn = (p.positionName || "").trim();
    const displayNorm = normalizePositionLabel(display);
    return (
      normalizePositionLabel(dn) === displayNorm ||
      normalizePositionLabel(pn) === displayNorm ||
      normalizePositionLabel(dn).startsWith(displayNorm + " ") ||
      normalizePositionLabel(pn).startsWith(displayNorm + " ")
    );
  };

  if (explicitShift) {
    const es = norm(explicitShift);
    return positions.find((p: any) => norm(p.shift) === es && matchOne(p)) || null;
  }

  const allDay = getSchedulesForPartnerDay(partner.id, day);
  const displayNorm = normalizePositionLabel(display);
  for (const s of allDay) {
    const dn = (s.positionDisplayName || s.positionName || "").trim();
    if (!dn) continue;
    const dnNorm = normalizePositionLabel(dn);
    if (
      dnNorm !== displayNorm &&
      !dnNorm.startsWith(displayNorm + " ") &&
      !(displayNorm.length >= 3 && dnNorm.includes(displayNorm))
    ) {
      continue;
    }
    const sid = Number(s.position?.id ?? s.position);
    if (Number.isNaN(sid)) continue;
    const shiftName = normalizeShiftName(s.shift);
    const schedDisp = (s.positionDisplayName || s.positionName || s.position?.name || "").trim();
    if (schedDisp) {
      const exact = positions.find(
        (p: any) =>
          Number(p.positionId) === sid &&
          norm(p.shift) === norm(shiftName) &&
          normalizePositionLabel(p.positionDisplayName || "") ===
            normalizePositionLabel(schedDisp),
      );
      if (exact) return exact;
    }
    const byIdShift = positions.find(
      (p: any) => Number(p.positionId) === sid && norm(p.shift) === norm(shiftName),
    );
    if (byIdShift) return byIdShift;
  }

  const matches = positions.filter(matchOne);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  const primary = partner.candidate?.shift?.name || "Matutino";
  return findPositionRecordForCellValue(part, primary, positions);
}

function resolvePositionRowFromScheduleRecord(
  s: any,
  positions: any[],
  shift: string,
): any | null {
  const sid = Number(s.position?.id ?? s.position);
  if (Number.isNaN(sid)) return null;
  const shiftNorm = normalizeShiftName(shift).toLowerCase();
  const schedDisp = (s.positionDisplayName || s.positionName || s.position?.name || "").trim();
  if (schedDisp) {
    const exact = positions.find(
      (p: any) =>
        Number(p.positionId) === sid &&
        normalizeShiftName(p.shift).toLowerCase() === shiftNorm &&
        normalizePositionLabel(p.positionDisplayName || "") ===
          normalizePositionLabel(schedDisp),
    );
    if (exact) return exact;
  }
  return (
    positions.find(
      (p: any) =>
        Number(p.positionId) === sid &&
        normalizeShiftName(p.shift).toLowerCase() === shiftNorm,
    ) || null
  );
}

function getPartnerIdFromScheduleRecord(s: any): number | undefined {
  const p = s?.partner;
  if (p == null) return undefined;
  if (typeof p === "number") return p;
  if (typeof p === "object" && p.id != null) return Number(p.id);
  return undefined;
}

function partnerIdsEqual(a: any, b: any): boolean {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

function getCrossShiftCardAccent(
  assignmentShift: any,
  palette: Record<string, string>,
): { borderLeft: string; backgroundColor: string; border: string } {
  const n = normalizeShiftName(assignmentShift).toLowerCase();
  let accent = palette.Vespertino ?? "#DC2C27";
  let bg = "rgba(220, 44, 39, 0.1)";
  if (n.includes("matutino")) {
    accent = palette.Matutino ?? "#F7B917";
    bg = "rgba(247, 185, 23, 0.12)";
  } else if (n.includes("intermedio")) {
    accent = palette.Intermedio ?? "#1E90FF";
    bg = "rgba(30, 144, 255, 0.12)";
  } else if (n.includes("vespertino")) {
    accent = palette.Vespertino ?? "#DC2C27";
    bg = "rgba(220, 44, 39, 0.12)";
  } else {
    accent = "#94a3b8";
    bg = "rgba(148, 163, 184, 0.12)";
  }
  return {
    borderLeft: `4px solid ${accent}`,
    backgroundColor: bg,
    border: `1px solid rgba(148, 163, 184, 0.35)`,
  };
}

function isCrossShiftAssignment(
  item: any,
  shiftRow: string,
): boolean {
  if (item?._crossShiftReadOnly) return true;
  const itemShift = normalizeShiftName(item?.shift);
  const rowShift = normalizeShiftName(shiftRow);
  return !!(itemShift && rowShift && itemShift !== rowShift);
}

/** Máx. 2 puestos del turno de la fila + puestos de otros turnos (solo lectura). */
function partitionDisplayItemsForShiftRow(
  items: any[],
  shiftRow: string,
): any[] {
  const rowShiftNorm = normalizeShiftName(shiftRow);
  const editable = items
    .filter((s: any) => normalizeShiftName(s.shift) === rowShiftNorm)
    .slice(0, 2);
  const cross = items.filter(
    (s: any) => normalizeShiftName(s.shift) !== rowShiftNorm,
  );

  if (cross.length === 0) return editable;
  if (editable.length === 0) return cross;

  const editableKeys = new Set(
    editable.map((s: any) => {
      const sid = Number(s.id ?? s.branchScheduleId);
      if (!Number.isNaN(sid) && sid > 0) return `id:${sid}`;
      const pid = normalizePositionIdKey(s.position?.id ?? s.positionId);
      return `p:${pid}|sh:${normalizeShiftName(s.shift)}`;
    }),
  );

  const crossOnly = cross.filter((s: any) => {
    const sid = Number(s.id ?? s.branchScheduleId);
    if (!Number.isNaN(sid) && sid > 0) return !editableKeys.has(`id:${sid}`);
    const pid = normalizePositionIdKey(s.position?.id ?? s.positionId);
    return !editableKeys.has(
      `p:${pid}|sh:${normalizeShiftName(s.shift)}`,
    );
  });

  return [...editable, ...crossOnly];
}

function displayItemToCellPart(item: any, rowShift: string): string {
  const name = String(item?.positionName || item?.position?.name || "").trim();
  if (!name) return "";
  const itemShift = normalizeShiftName(item?.shift);
  const rowNorm = normalizeShiftName(rowShift);
  if (itemShift && rowNorm && itemShift !== rowNorm) {
    return `${name} (${itemShift})`;
  }
  return name;
}

function seedCellValueBeforeAdd(
  cellValue: string,
  shift: string,
  displayItems: any[],
  belongsToShift: (part: string, shift: string) => boolean,
): string {
  const baseParts = splitWeeklyCellParts(cellValue);
  const sameShiftInBase = baseParts.filter((p) => belongsToShift(p, shift));
  const sameShiftDisplay = displayItems.filter(
    (d: any) => !isCrossShiftAssignment(d, shift),
  );
  if (sameShiftDisplay.length <= sameShiftInBase.length) {
    return cellValue;
  }
  const crossParts = baseParts.filter((p) => !belongsToShift(p, shift));
  const merged = [...crossParts];
  for (const item of sameShiftDisplay) {
    const label = displayItemToCellPart(item, shift);
    if (!label) continue;
    if (!merged.some((p) => p.toLowerCase() === label.toLowerCase())) {
      merged.push(label);
    }
  }
  return merged.join(", ");
}

function isPrimarySameShiftPositionCard(
  item: any,
  index: number,
  displayItems: any[],
  shiftRow: string,
): boolean {
  if (isCrossShiftAssignment(item, shiftRow)) return false;
  return (
    displayItems.length === 1 ||
    (displayItems.length >= 2 && index === 0)
  );
}

function getPositionCardSurfaceStyle(
  item: any,
  index: number,
  displayItems: any[],
  shiftRow: string,
  shiftColors: Record<string, string>,
): { backgroundColor: string; border: string; borderLeft: string } {
  if (isCrossShiftAssignment(item, shiftRow)) {
    return getCrossShiftCardAccent(item.shift, shiftColors);
  }

  if (isPrimarySameShiftPositionCard(item, index, displayItems, shiftRow)) {
    return {
      backgroundColor: "transparent",
      border: "none",
      borderLeft: "none",
    };
  }

  return {
    backgroundColor: "rgba(107, 114, 128, 0.06)",
    border: "1px solid rgba(107, 114, 128, 0.1)",
    borderLeft: "1px solid rgba(107, 114, 128, 0.1)",
  };
}

function partnerOccupiesExactTemplateDisplay(
  partner: any,
  day: string,
  shiftRow: string,
  templateDisplay: string,
  positions: any[],
  getSchedulesForPartnerDay: (pid: number, d: string, sh?: string) => any[],
  cellAssignments: Record<string, string>,
): boolean {
  const shiftNorm = normalizeShiftName(shiftRow).toLowerCase();
  const tgt = normalizePositionLabel(templateDisplay);
  const list = getSchedulesForPartnerDay(partner.id, day, shiftRow);
  for (const s of list) {
    const d = (s.positionDisplayName || s.positionName || "").trim();
    if (d && normalizePositionLabel(d) === tgt) return true;
  }
  const raw = (cellAssignments[`${partner.id}-${day}`] ?? "").trim();
  if (!raw || raw === "-" || raw === "DESCANSO") return false;
  const parts = raw.split(",").map((x) => x.trim()).filter(Boolean);
  for (const part of parts) {
    const pos = resolvePositionAssignmentForPartnerDay(
      part,
      partner,
      day,
      positions,
      getSchedulesForPartnerDay,
    );
    if (!pos) continue;
    if (normalizeShiftName(pos.shift).toLowerCase() !== shiftNorm) continue;
    const d = (pos.positionDisplayName || pos.positionName || "").trim();
    if (normalizePositionLabel(d) === tgt) return true;
  }
  return false;
}

function getEssentialDescansoCoverage(
  partner: any,
  day: string,
  shift: string,
  positionsByShift: Record<string, any[]>,
  allPartners: any[],
  cellAssignments: Record<string, string>,
  positions: any[],
  getSchedulesForPartnerDay: (pid: number, d: string, sh?: string) => any[],
): {
  required: number;
  covered: number;
  uncovered: number;
  coverNames: string[];
  isFullyCovered: boolean;
  hasEssentialRole: boolean;
} {
  const partnerPositionName = normalizePositionLabel(
    partner.candidate?.vacancy?.position?.name || "",
  );
  const essentialRows = (positionsByShift[shift] || []).filter(
    (p: any) =>
      p.isEssencial &&
      normalizePositionLabel(p.positionName || "") === partnerPositionName,
  );
  if (essentialRows.length === 0) {
    return {
      required: 0,
      covered: 0,
      uncovered: 0,
      coverNames: [],
      isFullyCovered: true,
      hasEssentialRole: false,
    };
  }
  const needRemaining = new Map<string, number>();
  essentialRows.forEach((row: any) => {
    const d = (row.positionDisplayName || row.positionName || "").trim();
    if (!d) return;
    needRemaining.set(d, (needRemaining.get(d) || 0) + 1);
  });
  const shiftNorm = normalizeShiftName(shift).toLowerCase();
  const coverNames: string[] = [];
  const consumeNeed = (pos: any, p: any) => {
    if (!pos) return;
    if (normalizeShiftName(pos.shift).toLowerCase() !== shiftNorm) return;
    const d = (pos.positionDisplayName || pos.positionName || "").trim();
    const n = needRemaining.get(d);
    if (n != null && n > 0) {
      needRemaining.set(d, n - 1);
      const nm = toCamelCase(
        `${p.person?.firstName || ""} ${p.person?.lastName || ""}`.trim()
      );
      coverNames.push(nm || `Colaborador ${p.id}`);
    }
  };
  for (const p of allPartners) {
    if (p.id === partner.id) continue;
    const raw = (cellAssignments[`${p.id}-${day}`] ?? "").trim();
    if (raw && raw !== "-" && raw !== "DESCANSO") {
      const parts = raw.split(",").map((x) => x.trim()).filter(Boolean);
      for (const part of parts) {
        const pos = resolvePositionAssignmentForPartnerDay(
          part,
          p,
          day,
          positions,
          getSchedulesForPartnerDay,
        );
        consumeNeed(pos, p);
      }
    } else {
      const listSched = getSchedulesForPartnerDay(p.id, day, shift);
      for (const s of listSched) {
        const pos = resolvePositionRowFromScheduleRecord(s, positions, shift);
        consumeNeed(pos, p);
      }
    }
  }
  let uncoveredCount = 0;
  needRemaining.forEach((n) => {
    uncoveredCount += n;
  });
  const required = essentialRows.length;
  const covered = required - uncoveredCount;
  return {
    required,
    covered,
    uncovered: uncoveredCount,
    coverNames,
    isFullyCovered: uncoveredCount === 0,
    hasEssentialRole: true,
  };
}

interface ConfigHorarioSemanalTableProps {
  positions: any[];
  schedules: any[];
  partners: any[];
  weekDays: string[];
  weekValue: string;
  branchId: number;
  availableRoles: string[];
  freeDays: any[];
  weekendSchedule: any;
  branchHours?: any;
  scheduleViewMode?: boolean;
  onDayClick?: (day: string, positionName?: string, positionShift?: string) => void;
  onUnsavedChange?: (hasChanges: boolean) => void;
}

const ConfigHorarioSemanalTable = forwardRef<{
  getTableData: () => any;
  fillWeekWithCurrentDay: () => boolean;
  syncWeekWithSchedule: (schedules: any[]) => boolean;
  exportCurrentTableAsPdf: (options: { branchName: string; weekName: string; notes?: string }) => Promise<void>;
  getWeeklySaveContext: () => {
    cellAssignments: Record<string, string>;
    initialCellAssignments: Record<string, string>;
    removedScheduleIds: number[];
  };
}, ConfigHorarioSemanalTableProps>(({
  positions,
  schedules,
  partners,
  weekDays,
  weekValue,
  branchId,
  availableRoles,
  freeDays,
  weekendSchedule,
  branchHours,
  scheduleViewMode = false,
  onDayClick,
  onUnsavedChange,
}, ref) => {
  const [editingCell, setEditingCell] = useState<{
    positionId: string;
    day: string;
  } | null>(null);

  // Vista por colaborador: asignación por (partnerId, day) para dropdown en cada celda
  const [cellAssignments, setCellAssignments] = useState<Record<string, string>>({});
  const [initialCellAssignments, setInitialCellAssignments] = useState<Record<string, string>>({});
  /** Último snapshot “guardado” para detectar celdas sucias sin depender de initialCellAssignments en el efecto (evita bucles). */
  const cellAssignmentsInitialSyncRef = useRef<Record<string, string>>({});
  const [scheduleCellEditing, setScheduleCellEditing] = useState<{
    partnerId: number;
    day: string;
    shift: string;
    mode: "replace" | "add";
    scheduleId?: number | null;
    replaceTargetLabel?: string | null;
  } | null>(null);
  const [cellFilterText, setCellFilterText] = useState("");
  const [hoveredCardKey, setHoveredCardKey] = useState<string | null>(null);
  const [hoveredScheduleCellKey, setHoveredScheduleCellKey] = useState<
    string | null
  >(null);
  const [addSlotVisibleCellKey, setAddSlotVisibleCellKey] = useState<
    string | null
  >(null);
  const addSlotHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [removedScheduleIds, setRemovedScheduleIds] = useState<Set<number>>(new Set());
  const scheduleDropdownRef = useRef<HTMLDivElement>(null);
  
  const rowRefs = useRef<{ [key: string]: { getRowData: () => any; fillWeekWithMatching: (partner: any, position: any) => boolean; syncDayWithSchedule: (day: string, schedule: any) => boolean } }>({});

  // Agrupar posiciones por turno
  const positionsByShift = useMemo(() => {
    const grouped: { [key: string]: any[] } = {
      Matutino: [],
      Intermedio: [],
      Vespertino: [],
    };

    positions.forEach((pos) => {
      if (grouped[pos.shift]) {
        grouped[pos.shift].push(pos);
      }
    });

    return grouped;
  }, [positions]);

  const getScheduleForPositionAndDay = (
    positionId: string,
    day: string
  ): any => {
    const dayDate = getDateByDayOfWeek(weekValue, day);
    if (!dayDate) return null;

    const formattedDate = moment(dayDate).format("YYYY-MM-DD");

    return schedules.find(
      (schedule) =>
        schedule.position?.id === Number(positionId.split("-")[0]) &&
        moment(schedule.exactDate).format("YYYY-MM-DD") === formattedDate
    );
  };

  const getScheduleItemForCell = (positionId: number, shift: string, day: string): any => {
    if (!schedules || schedules.length === 0) return null;
    return schedules.find(
      (s: any) =>
        s.position?.id === positionId &&
        s.shift === shift &&
        s.dayOfWeek === day
    ) || null;
  };

  /** Devuelve todos los horarios para una celda (posición + turno + día) para mostrar más de una persona. */
  const getScheduleItemsForCell = (positionId: number, shift: string, day: string): any[] => {
    if (!schedules || schedules.length === 0) return [];
    return schedules.filter(
      (s: any) =>
        s.position?.id === positionId &&
        s.shift === shift &&
        s.dayOfWeek === day
    );
  };

  // Obtener partner desde weekendSchedule para una posición específica
  const getPartnerFromSchedule = (position: any, shift: string): any => {
    if (!weekendSchedule?.items) return null;
    
    // Buscar el primer item que tenga esta posición y turno
    const item = weekendSchedule.items.find(
      (item: any) => 
        item.position?.id === position.positionId && 
        item.shift === shift &&
        item.partner
    );
    
    return item?.partner || null;
  };

  // Obtener colaborador que hace match con la posición basándose en candidate.vacancy.position y shift
  const getPartnerForPosition = (position: any): any => {
    if (!partners || partners.length === 0) return null;

    // Normalizar el nombre de la posición para comparación (sin espacios extra, lowercase)
    const normalizeName = (name: string) => {
      if (!name) return "";
      return name.trim().toLowerCase();
    };

    const positionNameNormalized = normalizeName(position.positionName);
    const shiftNormalized = normalizeName(position.shift);

    // Buscar partners que hagan match con el puesto y turno
    const matchedPartners = partners.filter((partner) => {
      if (!partner.candidate?.vacancy?.position?.name || !partner.candidate?.shift?.name) {
        return false;
      }

      const partnerPositionName = normalizeName(partner.candidate.vacancy.position.name);
      const partnerShiftName = normalizeName(partner.candidate.shift.name);

      // Primero intentar match exacto
      if (partnerPositionName === positionNameNormalized && partnerShiftName === shiftNormalized) {
        return true;
      }

      // Si no hay match exacto, intentar match parcial (para casos como "Comalero" vs "Comalero 1")
      // Remover números y espacios extra para comparación más flexible
      const cleanPositionName = positionNameNormalized.replace(/\s+\d+$/, "").trim();
      const cleanPartnerPositionName = partnerPositionName.replace(/\s+\d+$/, "").trim();

      const positionMatches = cleanPositionName === cleanPartnerPositionName;
      const shiftMatches = partnerShiftName === shiftNormalized;

      return positionMatches && shiftMatches;
    });

    // Retornar el primer match si existe
    return matchedPartners.length > 0 ? matchedPartners[0] : null;
  };

  const shifts = ["Matutino", "Intermedio", "Vespertino"];

  const shiftColors: { [key: string]: string } = {
    Matutino: "#F7B917",
    Intermedio: "#1E90FF",
    Vespertino: "#DC2C27",
  };

  const shiftBgColors: { [key: string]: string } = {
    Matutino: "rgba(247, 185, 23, 0.15)",
    Intermedio: "rgba(30, 144, 255, 0.15)",
    Vespertino: "rgba(220, 44, 39, 0.15)",
  };

  const getShiftHoursLabel = useCallback(
    (shift: string): string => {
      if (!branchHours) return "";

      const formatTime = (time: string): string => {
        if (!time) return "";
        const clean = String(time).trim();
        // Esperamos formatos tipo "HH:mm" o "HH:mm:ss"; nos quedamos solo con HH:mm
        return clean.length >= 5 ? clean.slice(0, 5) : clean;
      };

      const normalized = shift.trim().toLowerCase();
      if (normalized === "matutino") {
        if (!branchHours.matutinoStart || !branchHours.matutinoEnd) return "";
        return `${formatTime(branchHours.matutinoStart)} - ${formatTime(
          branchHours.matutinoEnd,
        )}`;
      }
      if (normalized === "intermedio") {
        if (!branchHours.intermedioStart || !branchHours.intermedioEnd)
          return "";
        return `${formatTime(branchHours.intermedioStart)} - ${formatTime(
          branchHours.intermedioEnd,
        )}`;
      }
      if (normalized === "vespertino") {
        if (!branchHours.vespertinoStart || !branchHours.vespertinoEnd)
          return "";
        return `${formatTime(branchHours.vespertinoStart)} - ${formatTime(
          branchHours.vespertinoEnd,
        )}`;
      }
      return "";
    },
    [branchHours],
  );

  /** Formatea startTime/endTime: muestra solo el primero y el último (ej. "11:30 - 15:30"). */
  const formatScheduleTimeRange = useCallback((schedule: any): string => {
    if (!schedule) return "";
    const startRaw = String(schedule.startTime ?? "").trim();
    const endRaw = String(schedule.endTime ?? "").trim();
    const formatPart = (t: string) =>
      t.length >= 5 ? t.slice(0, 5) : t;
    const firstOf = (s: string) => {
      if (!s) return "";
      if (s.includes(" - ")) return formatPart(s.split(" - ")[0]?.trim() || s);
      return formatPart(s);
    };
    const lastOf = (s: string) => {
      if (!s) return "";
      if (s.includes(" - ")) {
        const parts = s.split(" - ").map((x: string) => x.trim());
        return formatPart(parts[parts.length - 1] || parts[0] || s);
      }
      return formatPart(s);
    };
    const inicio = firstOf(startRaw);
    const fin = lastOf(endRaw);
    if (inicio && fin) return `${inicio} - ${fin}`;
    if (inicio) return inicio;
    if (fin) return fin;
    return "";
  }, []);

  /** Todos los horarios de un colaborador en un día (varios si tiene doble puesto/función). */
  const getSchedulesForPartnerDay = useCallback(
    (partnerId: number, day: string, shift?: string): any[] => {
      if (!schedules?.length) return [];
      let list = schedules.filter((s: any) => {
        const pid = getPartnerIdFromScheduleRecord(s);
        return pid != null && partnerIdsEqual(pid, partnerId) && s.dayOfWeek === day;
      });
      if (shift) {
        const sn = normalizeShiftName(shift);
        list = list.filter(
          (s: any) => normalizeShiftName(s.shift) === sn
        );
      }
      list = list.filter((s: any) => {
        const sid = Number(s?.id);
        if (Number.isNaN(sid)) return true;
        return !removedScheduleIds.has(sid);
      });
      list = dedupeBranchSchedulesForDisplay(
        list,
        normalizeShiftName,
        getPartnerIdFromScheduleRecord,
      );
      return list.sort((a: any, b: any) => {
        const na = (a.positionName || a.position?.name || "").toLowerCase();
        const nb = (b.positionName || b.position?.name || "").toLowerCase();
        return na.localeCompare(nb, "es");
      });
    },
    [schedules, removedScheduleIds]
  );

  const getSchedulesForPartnerDayMatchingCell = useCallback(
    (
      partnerId: number,
      day: string,
      shift: string,
      cellPositionLabel: string
    ): any[] => {
      const trimmed = (cellPositionLabel || "").trim();
      if (!trimmed || trimmed === "-" || trimmed === "DESCANSO") return [];
      const byShift = getSchedulesForPartnerDay(partnerId, day, shift);
      if (byShift.length > 0) return byShift;
      const allDay = getSchedulesForPartnerDay(partnerId, day);
      const normalizePos = (s: string) =>
        s
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      const cv = normalizePos(trimmed);
      return allDay.filter((s: any) => {
        const pn = normalizePos(
          s.positionName || s.position?.name || ""
        );
        if (!pn) return false;
        return (
          pn === cv ||
          pn.includes(cv) ||
          cv.includes(pn)
        );
      });
    },
    [getSchedulesForPartnerDay]
  );

  const hasIndividualScheduleForPartnerDay = useCallback(
    (partnerId: number, day: string): boolean =>
      getSchedulesForPartnerDay(partnerId, day).length > 0,
    [getSchedulesForPartnerDay]
  );

  const hasAnyIndividualScheduleForPartner = useCallback(
    (partnerId: number): boolean =>
      weekDays.some((d) => getSchedulesForPartnerDay(partnerId, d).length > 0),
    [weekDays, getSchedulesForPartnerDay]
  );

  // Vista por colaborador: para (partnerId, day) devuelve el puesto o "DESCANSO" o ""
  const getDisplayForPartnerDay = useCallback(
    (partnerId: number, day: string): string => {
      const individualList = getSchedulesForPartnerDay(partnerId, day);
      if (individualList.length > 0) {
        const shiftNorms = new Set(
          individualList.map((s: any) => normalizeShiftName(s.shift)),
        );
        const multiShift = shiftNorms.size > 1;
        const parts = individualList
          .map((s: any) => {
            const name =
              s.positionDisplayName ||
              s.positionName ||
              s.position?.name ||
              "";
            if (!name) return "";
            if (multiShift && s.shift) {
              return `${name} (${normalizeShiftName(s.shift)})`;
            }
            return name;
          })
          .filter(Boolean);
        return parts.join(", ");
      }

      const dayItems =
        weekendSchedule?.items?.filter(
          (item: any) => item.partner?.id === partnerId && item.day === day
        ) || [];

      const withPosition = dayItems.filter((item: any) => item.position);
      if (withPosition.length > 0) {
        const names = withPosition.map(
          (item: any) => item.positionDisplayName || item.position?.name || ""
        ).filter(Boolean);
        return [...new Set(names)].join(", ") || names[0] || "";
      }

      const freeDayEntry = freeDays?.find((fd: any) => fd.partner?.id === partnerId);
      if (freeDayEntry?.days?.includes(day)) return "DESCANSO";

      const hasRestDay = dayItems.some((item: any) => item.freeDay);
      if (hasRestDay) return "DESCANSO";

      return "";
    },
    [freeDays, weekendSchedule, getSchedulesForPartnerDay]
  );

  const buildScheduleViewDisplayItems = useCallback(
    (partnerId: number, day: string, shiftRow: string, cellValue: string): any[] => {
      const cellKey = `${partnerId}-${day}`;
      const cellTrim = (cellValue || "").trim();
      const initTrim = (initialCellAssignments[cellKey] ?? "").trim();
      const cellDirty =
        scheduleViewMode &&
        cellTrim !== initTrim &&
        cellTrim !== "" &&
        cellTrim !== "-" &&
        cellTrim !== "DESCANSO";

      if (cellDirty) {
        const partnerRow = partners.find((x: any) => Number(x.id) === Number(partnerId));
        if (partnerRow) {
          const parts = cellTrim.split(",").map((x) => x.trim()).filter(Boolean);
          const items: any[] = [];
          for (const part of parts) {
            const picked = resolvePositionAssignmentForPartnerDay(
              part,
              partnerRow,
              day,
              positions,
              getSchedulesForPartnerDay,
            );
            if (!picked) continue;
            const label = getShiftHoursLabel(picked.shift);
            const cross =
              normalizeShiftName(picked.shift) !== normalizeShiftName(shiftRow);
            items.push({
              positionName: picked.positionDisplayName || picked.positionName || part,
              position: {
                id: picked.positionId,
                name: picked.positionName,
              },
              positionId: picked.positionId,
              shift: picked.shift,
              startTime: label,
              endTime: label,
              _crossShiftReadOnly: cross,
              _pendingFromCell: true,
              _cellPartLabel: part,
            });
          }
          if (items.length > 0) {
            return partitionDisplayItemsForShiftRow(items, shiftRow);
          }
        }
      }

      const hasIndividualDay = hasIndividualScheduleForPartnerDay(
        partnerId,
        day,
      );

      // Importante: si quitas solo la tarjeta del turno de esta fila pero queda un
      // "otro turno", assignments (mismo turno) queda [] y antes no entrábamos aquí;
      // caíamos en rutas sin id y la tarjeta gris reaparecía. Partir siempre allDay.
      if (hasIndividualDay) {
        const allDay = getSchedulesForPartnerDay(partnerId, day);
        const rowShiftNorm = normalizeShiftName(shiftRow);
        const editable = allDay
          .filter(
            (s: any) => normalizeShiftName(s.shift) === rowShiftNorm,
          )
          .map((s: any) => ({
            ...s,
            _crossShiftReadOnly: false,
          }));
        const cross = allDay
          .filter(
            (s: any) => normalizeShiftName(s.shift) !== rowShiftNorm,
          )
          .map((s: any) => ({ ...s, _crossShiftReadOnly: true }));

        if (cross.length === 0) return editable;
        if (editable.length === 0) return cross;

        const editableKeys = new Set(
          editable.map((s: any) => {
            const sid = Number(s.id);
            if (!Number.isNaN(sid)) return `id:${sid}`;
            const pid = normalizePositionIdKey(s.position?.id ?? s.positionId);
            return `p:${pid}|sh:${normalizeShiftName(s.shift)}`;
          }),
        );

        const crossOnly = cross.filter((s: any) => {
          const sid = Number(s.id);
          if (!Number.isNaN(sid)) return !editableKeys.has(`id:${sid}`);
          const pid = normalizePositionIdKey(s.position?.id ?? s.positionId);
          return !editableKeys.has(
            `p:${pid}|sh:${normalizeShiftName(s.shift)}`,
          );
        });

        return partitionDisplayItemsForShiftRow(
          [...editable, ...crossOnly],
          shiftRow,
        );
      }

      if (cellTrim && cellTrim !== "DESCANSO") {
        const partnerRow = partners.find((x: any) => Number(x.id) === Number(partnerId));
        const picked = partnerRow
          ? resolvePositionAssignmentForPartnerDay(
              cellTrim,
              partnerRow,
              day,
              positions,
              getSchedulesForPartnerDay,
            )
          : findPositionRecordForCellValue(cellTrim, shiftRow, positions);
        if (picked) {
          const listPicked = getSchedulesForPartnerDay(partnerId, day, picked.shift);
          const pid = Number(picked.positionId);
          const hasApiForPick = listPicked.some(
            (s: any) => Number(s.position?.id ?? s.position) === pid,
          );
          if (!hasApiForPick) {
            const label = getShiftHoursLabel(picked.shift);
            return [
              {
                positionName:
                  picked.positionDisplayName || picked.positionName || cellTrim,
                position: {
                  id: picked.positionId,
                  name: picked.positionName,
                },
                positionId: picked.positionId,
                shift: picked.shift,
                startTime: label,
                endTime: label,
                _crossShiftReadOnly: false,
                _pendingFromCell: true,
              },
            ];
          }
        }
      }

      if (cellValue && cellValue !== "DESCANSO") {
        const matched = getSchedulesForPartnerDayMatchingCell(
          partnerId,
          day,
          shiftRow,
          cellValue,
        );
        if (matched.length > 0) {
          return matched.map((s: any) => ({ ...s, _crossShiftReadOnly: false }));
        }
        return [
          {
            positionName: cellValue,
            position: { name: cellValue },
            startTime: "",
            endTime: "",
            _crossShiftReadOnly: false,
          },
        ];
      }
      return [];
    },
    [
      getSchedulesForPartnerDay,
      getSchedulesForPartnerDayMatchingCell,
      hasIndividualScheduleForPartnerDay,
      positionsByShift,
      getShiftHoursLabel,
      positions,
      partners,
      initialCellAssignments,
      scheduleViewMode,
    ],
  );

  /** Asignación en la fila rowIndex para (partnerId, day, shift); usado para doble turno/puesto. */
  const getAssignmentForPartnerDay = useCallback(
    (partnerId: number, day: string, rowIndex: number, shift: string): any => {
      const list = getSchedulesForPartnerDay(partnerId, day, shift);
      return list[rowIndex] || null;
    },
    [getSchedulesForPartnerDay]
  );

  /** Número de filas a mostrar para un colaborador en un turno (máx. asignaciones en un día). */
  const getPartnerRowCount = useCallback(
    (partnerId: number, shift: string): number => {
      let max = 0;
      weekDays.forEach((day) => {
        const n = getSchedulesForPartnerDay(partnerId, day, shift).length;
        if (n > max) max = n;
      });
      return Math.max(1, max);
    },
    [weekDays, getSchedulesForPartnerDay]
  );

  /** Nombre del puesto que tiene el colaborador en este turno (para mostrar en bloque de otro turno). */
  const getPositionNameForPartnerInShift = useCallback(
    (partnerId: number, shift: string): string => {
      for (const day of weekDays) {
        const list = getSchedulesForPartnerDay(partnerId, day, shift);
        if (list.length > 0) {
          const first = list[0];
          return first.positionName || first.position?.name || "";
        }
      }
      if (hasAnyIndividualScheduleForPartner(partnerId)) {
        return "";
      }
      const items = weekendSchedule?.items?.filter(
        (item: any) => item.partner?.id === partnerId && item.position
      ) || [];
      const shiftPositions = positionsByShift[shift] || [];
      for (const item of items) {
        const posId = item.position?.id ?? item.positionId;
        const inShift = shiftPositions.some((p: any) => p.positionId === posId || p.positionId === String(posId));
        if (inShift) {
          return item.positionDisplayName || item.position?.name || "";
        }
      }
      return "";
    },
    [weekDays, getSchedulesForPartnerDay, weekendSchedule?.items, positionsByShift, hasAnyIndividualScheduleForPartner]
  );

  /** Horario formateado para (partnerId, day) en la fila rowIndex (por doble turno/puesto). */
  const getScheduleTimeForPartnerDay = useCallback(
    (partnerId: number, day: string, rowIndex: number = 0, shift?: string): string => {
      const list = getSchedulesForPartnerDay(partnerId, day, shift);
      const schedule = list[rowIndex];
      return formatScheduleTimeRange(schedule);
    },
    [getSchedulesForPartnerDay, formatScheduleTimeRange]
  );

  // Colaboradores solo por su turno principal (candidate.shift): cada persona aparece en un solo bloque
  const partnersByShift = useMemo(() => {
    if (!partners || !Array.isArray(partners)) {
      return { Matutino: [], Intermedio: [], Vespertino: [] };
    }
    const grouped: { [key: string]: any[] } = {
      Matutino: [],
      Intermedio: [],
      Vespertino: [],
    };
    const shiftOrder: { [key: string]: string } = {
      matutino: "Matutino",
      intermedio: "Intermedio",
      vespertino: "Vespertino",
    };
    partners.forEach((p: any) => {
      const rawShift = p.candidate?.shift?.name;
      const normalized = (rawShift || "").trim().toLowerCase();
      const shiftKey = shiftOrder[normalized] || "Matutino";
      grouped[shiftKey].push(p);
    });
    const orderByPositionKey = new Map<string, number>();
    (positions || []).forEach((p: any) => {
      const key = normalizePositionOrderKey(p.positionName || p.positionDisplayName || "");
      if (!key) return;
      const ord = Number(p.positionOrder);
      if (!Number.isFinite(ord) || ord <= 0) return;
      const prev = orderByPositionKey.get(key);
      if (prev == null || ord < prev) orderByPositionKey.set(key, ord);
    });
    ["Matutino", "Intermedio", "Vespertino"].forEach((shift) => {
      (grouped[shift] || []).sort((a: any, b: any) => {
        const keyA = normalizePositionOrderKey(
          a?.candidate?.vacancy?.position?.name || "",
        );
        const keyB = normalizePositionOrderKey(
          b?.candidate?.vacancy?.position?.name || "",
        );
        const mapOrderA = keyA ? orderByPositionKey.get(keyA) : undefined;
        const mapOrderB = keyB ? orderByPositionKey.get(keyB) : undefined;
        const orderA = Number.isFinite(mapOrderA)
          ? Number(mapOrderA)
          : Number(a?.candidate?.vacancy?.position?.order);
        const orderB = Number.isFinite(mapOrderB)
          ? Number(mapOrderB)
          : Number(b?.candidate?.vacancy?.position?.order);
        const safeOrderA = Number.isFinite(orderA) ? orderA : Number.MAX_SAFE_INTEGER;
        const safeOrderB = Number.isFinite(orderB) ? orderB : Number.MAX_SAFE_INTEGER;
        if (safeOrderA !== safeOrderB) return safeOrderA - safeOrderB;
        const nameA = `${a.person?.firstName || ""} ${a.person?.lastName || ""}`.trim();
        const nameB = `${b.person?.firstName || ""} ${b.person?.lastName || ""}`.trim();
        return nameA.localeCompare(nameB, "es");
      });
    });
    return grouped;
  }, [partners, positions]);

  // Puestos esenciales (amarillo en dropdown), por positionDisplayName
  const essentialPositionDisplayNames = useMemo(() => {
    const set = new Set<string>();
    shifts.forEach((shift) => {
      (positionsByShift[shift] || []).forEach((p: any) => {
        if (p.isEssencial) {
          const name = p.positionDisplayName || p.positionName;
          if (name) set.add(name);
        }
      });
    });
    return set;
  }, [positionsByShift]);

  /** Vista colaborador: opciones por turno (plantilla activa del turno y con cupo > 0). */
  const cellDropdownOptionsByShift = useMemo(() => {
    const opts: { [key: string]: string[] } = {
      Matutino: ["DESCANSO"],
      Intermedio: ["DESCANSO"],
      Vespertino: ["DESCANSO"],
    };
    shifts.forEach((shift) => {
      const names = (positionsByShift[shift] || [])
        .filter((p: any) => Number(p.numberOfWorkers) > 0)
        .map((p: any) => p.positionDisplayName || p.positionName)
        .filter(Boolean);
      opts[shift] = ["DESCANSO", ...names];
    });
    return opts;
  }, [positionsByShift]);

  /**
   * Vista colaborador: todos los puestos de plantilla (cupo > 0) por turno.
   * Se deduplica por (turno + etiqueta). Si el mismo texto existe en más de un turno, el valor interno
   * lleva SHIFT_OPTION_MARK al final (invisible); la UI muestra solo el nombre (parseWeeklyCellDropdownOption).
   */
  const cellDropdownOptionsAll = useMemo(() => {
    const rows: { label: string; shift: string }[] = [];
    const seen = new Set<string>();
    SCHEDULE_VIEW_SHIFT_ORDER.forEach((sh) => {
      (positionsByShift[sh] || [])
        .filter((p: any) => Number(p.numberOfWorkers) > 0)
        .forEach((p: any) => {
          const d = (p.positionDisplayName || p.positionName || "").trim();
          if (!d) return;
          const key = `${sh}|${d}`;
          if (seen.has(key)) return;
          seen.add(key);
          rows.push({ label: d, shift: sh });
        });
    });
    const labelCount = new Map<string, number>();
    rows.forEach((r) => {
      labelCount.set(r.label, (labelCount.get(r.label) || 0) + 1);
    });
    const optStrings = rows.map((r) =>
      (labelCount.get(r.label) || 0) > 1
        ? `${r.label}${SHIFT_OPTION_MARK[r.shift] ?? ""}`
        : r.label,
    );
    return ["DESCANSO", ...optStrings.sort((a, b) => a.localeCompare(b, "es"))];
  }, [positionsByShift]);

  // Por turno: para cada positionName, cuántos slots tiene la plantilla y qué displayNames pertenecen (ocultar todo el puesto cuando ya se cubrieron todos los slots)
  const positionGroupByShift = useMemo(() => {
    const map: {
      [shift: string]: {
        positionNameToSlots: Record<string, number>;
        displayNamesByPositionName: Record<string, string[]>;
        displayNameToPositionName: Record<string, string>;
      };
    } = {};
    shifts.forEach((shift) => {
      const positionNameToSlots: Record<string, number> = {};
      const displayNamesByPositionName: Record<string, string[]> = {};
      const displayNameToPositionName: Record<string, string> = {};
      (positionsByShift[shift] || []).forEach((p: any) => {
        const name = p.positionName || "";
        const displayName = p.positionDisplayName || p.positionName || "";
        if (!name || !displayName) return;
        positionNameToSlots[name] = Math.max(
          positionNameToSlots[name] ?? 0,
          Number(p.numberOfWorkers) ?? 1
        );
        if (!displayNamesByPositionName[name]) displayNamesByPositionName[name] = [];
        if (!displayNamesByPositionName[name].includes(displayName)) {
          displayNamesByPositionName[name].push(displayName);
        }
        displayNameToPositionName[displayName] = name;
      });
      map[shift] = { positionNameToSlots, displayNamesByPositionName, displayNameToPositionName };
    });
    return map;
  }, [positionsByShift]);

  const cellPartBelongsToShift = useCallback(
    (part: string, rowShift: string): boolean => {
      const { explicitShift } = parseWeeklyCellDropdownOption(part);
      if (explicitShift) {
        return (
          normalizeShiftName(explicitShift).toLowerCase() ===
          normalizeShiftName(rowShift).toLowerCase()
        );
      }
      const pos = findPositionRecordForCellValue(part, rowShift, positions);
      if (!pos?.shift) return false;
      return (
        normalizeShiftName(pos.shift).toLowerCase() ===
        normalizeShiftName(rowShift).toLowerCase()
      );
    },
    [positions],
  );

  const formatOptionForShiftRow = useCallback(
    (opt: string, rowShift: string): string => {
      if (opt === "DESCANSO" || opt === "-") return opt;
      const { display, explicitShift } = parseWeeklyCellDropdownOption(opt);
      const optShift =
        resolveShiftForSlotFilterOption(
          display,
          explicitShift,
          rowShift,
          positionGroupByShift,
          SCHEDULE_VIEW_SHIFT_ORDER,
        ) || rowShift;
      if (
        explicitShift ||
        normalizeShiftName(optShift).toLowerCase() ===
          normalizeShiftName(rowShift).toLowerCase()
      ) {
        return opt;
      }
      return `${display} (${optShift})`;
    },
    [positionGroupByShift],
  );

  const applyScheduleCellOption = useCallback(
    (
      opt: string,
      ctx: {
        cellKey: string;
        cellValue: string;
        mode: "replace" | "add";
        shift: string;
        displayItems: any[];
        replaceScheduleId?: number | null;
        replaceTargetLabel?: string | null;
      },
    ) => {
      const { cellKey, mode, shift, displayItems } = ctx;
      const cellTrim = (ctx.cellValue || "").trim();

      if (opt === "DESCANSO") {
        const idsToRemove = displayItems
          .map((di: any) => Number(di?.id ?? di?.branchScheduleId))
          .filter((id: number) => !Number.isNaN(id) && id > 0);
        if (idsToRemove.length > 0) {
          setRemovedScheduleIds((prev) => {
            const next = new Set(prev);
            idsToRemove.forEach((id) => next.add(id));
            return next;
          });
        }
        setCellAssignments((prev) => ({ ...prev, [cellKey]: "DESCANSO" }));
        return;
      }

      if (opt === "-") {
        const editableItems = displayItems.filter(
          (d: any) => !d._crossShiftReadOnly,
        );
        const idsToRemove = editableItems
          .map((di: any) => Number(di?.id ?? di?.branchScheduleId))
          .filter((id: number) => !Number.isNaN(id) && id > 0);
        if (idsToRemove.length > 0) {
          setRemovedScheduleIds((prev) => {
            const next = new Set(prev);
            idsToRemove.forEach((id) => next.add(id));
            return next;
          });
        }
        const remaining = splitWeeklyCellParts(cellTrim).filter(
          (part) => !cellPartBelongsToShift(part, shift),
        );
        setCellAssignments((prev) => ({
          ...prev,
          [cellKey]: remaining.length ? remaining.join(", ") : "",
        }));
        return;
      }

      const partToStore = formatOptionForShiftRow(opt, shift);

      if (mode === "add") {
        setCellAssignments((prev) => {
          const stored = (prev[cellKey] ?? "").trim();
          let base = seedCellValueBeforeAdd(
            stored || cellTrim,
            shift,
            displayItems,
            cellPartBelongsToShift,
          );
          const baseParts = splitWeeklyCellParts(base);
          const sameShiftCount = countWeeklyCellPartsForShift(
            base,
            shift,
            cellPartBelongsToShift,
          );
          const crossCount = baseParts.filter(
            (p) => !cellPartBelongsToShift(p, shift),
          ).length;
          if (sameShiftCount >= 2) return prev;
          if (sameShiftCount + crossCount >= 2) return prev;

          return {
            ...prev,
            [cellKey]: appendWeeklyCellPart(base, partToStore),
          };
        });
        return;
      }

      const replaceId = Number(ctx.replaceScheduleId);
      const replaceTargetHint = String(ctx.replaceTargetLabel ?? "").trim();
      const idsToRemove: number[] = [];
      if (!Number.isNaN(replaceId) && replaceId > 0) {
        idsToRemove.push(replaceId);
      } else if (!replaceTargetHint) {
        displayItems
          .filter((d: any) => !d._crossShiftReadOnly)
          .forEach((di: any) => {
            const id = Number(di?.id ?? di?.branchScheduleId);
            if (!Number.isNaN(id) && id > 0) idsToRemove.push(id);
          });
      }
      if (idsToRemove.length > 0) {
        setRemovedScheduleIds((prev) => {
          const next = new Set(prev);
          idsToRemove.forEach((id) => next.add(id));
          return next;
        });
      }

      let newCellValue: string;
      const replaceTarget =
        String(ctx.replaceTargetLabel ?? "").trim() ||
        (() => {
          if (Number.isNaN(replaceId) || replaceId <= 0) return "";
          const targetItem = displayItems.find(
            (d: any) => Number(d?.id ?? d?.branchScheduleId) === replaceId,
          );
          return targetItem
            ? String(
                targetItem._cellPartLabel ||
                  displayItemToCellPart(targetItem, shift),
              ).trim()
            : "";
        })();

      if (replaceTarget) {
        newCellValue = replaceOneWeeklyCellPart(
          cellTrim,
          replaceTarget,
          partToStore,
        );
      } else {
        newCellValue = replaceWeeklyCellPartsForShift(
          cellTrim,
          shift,
          [partToStore],
          cellPartBelongsToShift,
        );
      }
      setCellAssignments((prev) => ({
        ...prev,
        [cellKey]: newCellValue,
      }));
    },
    [cellPartBelongsToShift, formatOptionForShiftRow],
  );

  // Doble clic en celda colaborador: rellenar solo días vacíos con su puesto; segundo doble clic: limpiar solo las celdas que tienen su puesto (no tocar las que ya tenían otro puesto)
  const handleCollaboratorCellDoubleClick = useCallback(
    (partner: any) => {
      const shiftName = partner.candidate?.shift?.name || "Matutino";
      const basePositionName =
        (partner.candidate?.vacancy?.position?.name || "").trim();
      const shiftPositions = positionsByShift[shiftName] || [];
      const match = shiftPositions.find(
        (p: any) =>
          p.positionName === basePositionName ||
          p.positionDisplayName === basePositionName ||
          (basePositionName &&
            (p.positionDisplayName || p.positionName || "").startsWith(basePositionName))
      );
      const positionValue = match
        ? (match.positionDisplayName || match.positionName || "")
        : basePositionName || "-";

      setCellAssignments((prev) => {
        const next = { ...prev };
        weekDays.forEach((day) => {
          const key = `${partner.id}-${day}`;
          const current = (prev[key] ?? "").trim();
          const isEmpty = !current || current === "-";
          if (positionValue === "-") return;
          if (isEmpty) {
            next[key] = positionValue;
          } else if (current === positionValue) {
            next[key] = "-";
          }
        });
        return next;
      });
    },
    [weekDays, positionsByShift]
  );

  const filteredCellOptions = useMemo(
    () => {
      const rowShift = scheduleCellEditing?.shift;
      const day = scheduleCellEditing?.day;
      const currentPartnerId = scheduleCellEditing?.partnerId;
      if (!rowShift) return [];
      const baseOptions = cellDropdownOptionsAll;
      if (baseOptions.length === 0) return [];
      const currentCellValue =
        day != null && currentPartnerId != null
          ? (cellAssignments[`${currentPartnerId}-${day}`] ?? "").trim() || "-"
          : "-";

      // Por día y turno de la fila: ocultar puestos cuando ya se cubrieron los slots de la plantilla.
      let options = baseOptions;
      if (day != null && currentPartnerId != null) {
        options = baseOptions.filter((opt) => {
          if (opt === "-" || opt === "DESCANSO") return true;
          const { display, explicitShift } = parseWeeklyCellDropdownOption(opt);
          const optShift = resolveShiftForSlotFilterOption(
            display,
            explicitShift,
            rowShift,
            positionGroupByShift,
            SCHEDULE_VIEW_SHIFT_ORDER,
          );
          if (!optShift) return true;
          const groupMeta = positionGroupByShift[optShift];
          if (!groupMeta) return true;
          const positionName = groupMeta.displayNameToPositionName[display];
          if (!positionName) return true;
          // Cada fila expandida (`... SUC 1`, `... SUC 2`) = 1 plaza; ocultar solo si esa etiqueta ya está tomada.
          let assignedForDisplay = 0;
          (partners || []).forEach((p: any) => {
            if (
              partnerOccupiesExactTemplateDisplay(
                p,
                day,
                optShift,
                display,
                positions,
                getSchedulesForPartnerDay,
                cellAssignments,
              )
            )
              assignedForDisplay += 1;
          });
          if (assignedForDisplay >= 1) return currentCellValue === opt;
          return true;
        });
      }

      // UX: en modo agregar, solo ocultar puestos ya asignados en este turno; en reemplazar, ocultar los de la celda.
      const editMode = scheduleCellEditing?.mode ?? "replace";
      const currentCellParts = splitWeeklyCellParts(currentCellValue);
      if (editMode === "add") {
        options = options.filter((opt) => {
          if (opt === "DESCANSO" || opt === "-") return false;
          const stored = formatOptionForShiftRow(opt, rowShift);
          return !currentCellParts.some(
            (part) =>
              part.toLowerCase() === stored.toLowerCase() ||
              part.toLowerCase() === opt.toLowerCase(),
          );
        });
      } else {
        options = options.filter(
          (opt) => opt !== currentCellValue && !currentCellParts.includes(opt),
        );
      }

      options = orderCellDropdownOptionsByRowShiftFirst(options, rowShift, positionGroupByShift);

      const normalize = (value: string) =>
        String(value)
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9\s]/g, "");

      const normalizedFilter = normalize(cellFilterText);
      if (!normalizedFilter) return options;

      const filtered = options.filter((opt) => {
        const textForSearch =
          opt === "DESCANSO" ? "DESCANSO" : parseWeeklyCellDropdownOption(opt).display;
        return normalize(textForSearch).includes(normalizedFilter);
      });
      return orderCellDropdownOptionsByRowShiftFirst(
        filtered,
        rowShift,
        positionGroupByShift,
      );
    },
    [
      cellDropdownOptionsAll,
      scheduleCellEditing?.shift,
      scheduleCellEditing?.day,
      scheduleCellEditing?.partnerId,
      cellFilterText,
      cellAssignments,
      partners,
      positionGroupByShift,
      partnersByShift,
      getSchedulesForPartnerDay,
      positionsByShift,
      positions,
      formatOptionForShiftRow,
    ]
  );

  useEffect(() => {
    if (!partners?.length) return;
    prefetchPartnerCurps(
      partners.map((p: any) => ({
        partnerId: Number(p.id),
        curp: p.person?.curp,
      })),
    );
  }, [partners]);

  // Sincronizar cellAssignments desde backend cuando cambien los datos
  useEffect(() => {
    if (!scheduleViewMode || !partners?.length) return;
    const next: Record<string, string> = {};
    partners.forEach((p: any) => {
      weekDays.forEach((day: string) => {
        const v = getDisplayForPartnerDay(p.id, day);
        next[`${p.id}-${day}`] = v || "";
      });
    });
    const prevInitial = cellAssignmentsInitialSyncRef.current;
    cellAssignmentsInitialSyncRef.current = next;
    setInitialCellAssignments(next);
    setCellAssignments((prev) => {
      const merged: Record<string, string> = { ...next };
      for (const key of Object.keys(prev)) {
        const init = (prevInitial[key] ?? "").trim();
        const curr = (prev[key] ?? "").trim();
        if (curr !== init) merged[key] = curr;
      }
      return merged;
    });
  }, [scheduleViewMode, partners, weekDays, getDisplayForPartnerDay]);

  useEffect(() => {
    // Solo al cambiar de sucursal/semana: si incluimos `schedules` aquí, cada refetch
    // borraba las eliminaciones locales y las tarjetas quitadas volvían a aparecer.
    setRemovedScheduleIds(new Set());
  }, [weekValue, branchId]);

  // Notificar al padre si hay cambios sin guardar
  useEffect(() => {
    if (!scheduleViewMode || !weekDays?.length) {
      onUnsavedChange?.(false);
      return;
    }
    const isEmpty = (v: string) => !(v ?? "").trim() || (v ?? "").trim() === "-";
    const hasChange = Object.keys(cellAssignments).some((key) => {
      const curr = (cellAssignments[key] ?? "").trim();
      const init = (initialCellAssignments[key] ?? "").trim();
      if (isEmpty(curr) && isEmpty(init)) return false;
      return curr !== init;
    });
    const hasRemoved = removedScheduleIds.size > 0;
    onUnsavedChange?.(hasChange || hasRemoved);
  }, [cellAssignments, initialCellAssignments, removedScheduleIds, scheduleViewMode, weekDays, onUnsavedChange]);

  const clearAddSlotHoverTimer = useCallback(() => {
    if (addSlotHoverTimerRef.current) {
      clearTimeout(addSlotHoverTimerRef.current);
      addSlotHoverTimerRef.current = null;
    }
  }, []);

  const scheduleAddSlotReveal = useCallback(
    (cellKey: string) => {
      clearAddSlotHoverTimer();
      addSlotHoverTimerRef.current = setTimeout(() => {
        setAddSlotVisibleCellKey(cellKey);
        addSlotHoverTimerRef.current = null;
      }, ADD_SLOT_HOVER_DELAY_MS);
    },
    [clearAddSlotHoverTimer],
  );

  useEffect(() => () => clearAddSlotHoverTimer(), [clearAddSlotHoverTimer]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        scheduleCellEditing &&
        scheduleDropdownRef.current &&
        !scheduleDropdownRef.current.contains(e.target as Node)
      ) {
        setScheduleCellEditing(null);
        setCellFilterText("");
        setHoveredScheduleCellKey(null);
        clearAddSlotHoverTimer();
        setAddSlotVisibleCellKey(null);
      }
    };
    if (scheduleCellEditing) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [scheduleCellEditing, clearAddSlotHoverTimer]);

  useImperativeHandle(ref, () => ({
    getTableData: () => {
      const items: any[] = [];
      const restDaysMap: { [partnerId: number]: string[] } = {};

      if (scheduleViewMode && partners?.length) {
        const itemKeys = new Set<string>();

        const pushItem = (item: any) => {
          const sid = Number(item.branchScheduleId);
          if (!Number.isNaN(sid) && sid > 0) {
            const sk = `sid:${sid}`;
            if (itemKeys.has(sk)) return;
            itemKeys.add(sk);
            items.push(item);
            return;
          }
          const k = `${item.partnerId}|${item.day}|${String(item.shift ?? "").trim().toLowerCase()}|${normalizePositionIdKey(item.positionId)}`;
          if (itemKeys.has(k)) return;
          itemKeys.add(k);
          items.push(item);
        };

        const enrichItemWithBranchScheduleId = (
          item: any,
          partnerId: number,
          day: string,
        ) => {
          if (item.branchScheduleId != null) return item;
          const shiftNorm = normalizeShiftName(item.shift).toLowerCase();
          const posId = Number(item.positionId);
          const label = item.positionDisplayName || item.positionName || "";
          const match = (schedules || []).find((s: any) => {
            const pid = getPartnerIdFromScheduleRecord(s);
            const sid = Number(s?.id);
            if (pid == null || !partnerIdsEqual(pid, partnerId)) return false;
            if (s.dayOfWeek !== day) return false;
            if (!Number.isNaN(sid) && removedScheduleIds.has(sid)) return false;
            if (normalizeShiftName(s.shift).toLowerCase() !== shiftNorm) {
              return false;
            }
            if (!Number.isNaN(posId) && posId > 0) {
              const sPos = Number(s.position?.id ?? s.position);
              if (sPos !== posId) return false;
            }
            if (label) {
              const sName = s.positionName || s.position?.name || "";
              if (!weeklyCellPartMatchesScheduleName(label, sName)) {
                return false;
              }
            }
            return true;
          });
          if (match?.id != null) {
            return { ...item, branchScheduleId: match.id };
          }
          return item;
        };

        partners.forEach((partner: any) => {
          const shiftName = partner.candidate?.shift?.name || "Matutino";
          weekDays.forEach((day: string) => {
            const key = `${partner.id}-${day}`;
            const value = (cellAssignments[key] ?? "").trim();
            if (!value || value === "-") return;
            if (value === "DESCANSO") {
              if (!restDaysMap[partner.id]) restDaysMap[partner.id] = [];
              restDaysMap[partner.id].push(day);
              pushItem({
                positionId: null,
                positionName: null,
                positionDisplayName: "Descanso",
                shift: shiftName,
                day,
                partnerId: partner.id,
                freeDayId: null,
              });
              return;
            }
            const parts = splitWeeklyCellParts(value);
            for (const part of parts) {
              const pos = findPositionRecordForCellValue(
                part,
                shiftName,
                positions,
              );
              if (!pos) continue;
              pushItem(
                enrichItemWithBranchScheduleId(
                  {
                    positionId: pos.positionId,
                    positionName: pos.positionName,
                    positionDisplayName:
                      pos.positionDisplayName || pos.positionName,
                    shift: pos.shift ?? shiftName,
                    day,
                    partnerId: partner.id,
                  },
                  partner.id,
                  day,
                ),
              );
            }
          });
        });
        const itemsDeduped = dedupeWeeklyTableItems(
          preferItemsWithBranchScheduleId(items),
        );
        // Un registro por colaborador: si falta, el backend no borraba FreeDay al quitar todo DESCANSO
        const restDays = (partners || []).map((partner: any) => ({
          partnerId: partner.id,
          days: (restDaysMap[partner.id] || []) as string[],
        }));
        return { items: itemsDeduped, restDays };
      }

      Object.values(rowRefs.current).forEach((rowRef) => {
        if (rowRef && rowRef.getRowData) {
          const rowData = rowRef.getRowData();
          if (rowData) {
            rowData.items.forEach((item: any) => {
              if (item.partnerId && item.role && item.role !== "N/A" && item.role !== "") {
                if (item.role === "DESCANSO") {
                  if (!restDaysMap[item.partnerId]) {
                    restDaysMap[item.partnerId] = [];
                  }
                  restDaysMap[item.partnerId].push(item.day);
                  
                  items.push({
                    positionId: null,
                    positionName: null,
                    positionDisplayName: "Descanso",
                    shift: item.shift,
                    day: item.day,
                    partnerId: item.partnerId,
                    freeDayId: item.freeDayId || null,
                  });
                } else {
                  items.push({
                    positionId: item.positionId,
                    positionName: item.positionName,
                    positionDisplayName: item.positionDisplayName,
                    shift: item.shift,
                    day: item.day,
                    partnerId: item.partnerId,
                  });
                }
              }
            });
          }
        }
      });

      const restDays = Object.entries(restDaysMap).map(([partnerId, days]) => ({
        partnerId: Number(partnerId),
        days: days as string[],
      }));

      return { items, restDays };
    },
    fillWeekWithCurrentDay: () => {
      // Rellenar la semana con partners que hacen match y sus posiciones
      let filled = false;
      
      Object.keys(rowRefs.current).forEach((positionId) => {
        const rowRef = rowRefs.current[positionId];
        if (!rowRef || !rowRef.fillWeekWithMatching) return;

        // Obtener la posición correspondiente
        const position = positions.find((p: any) => p.id === positionId);
        if (!position) return;

        // Buscar partner que hace match
        const matchedPartner = getPartnerForPosition(position);
        if (matchedPartner && rowRef.fillWeekWithMatching(matchedPartner, position)) {
          filled = true;
        }
      });
      
      return filled;
    },
    syncWeekWithSchedule: (schedules: any[]) => {
      if (!schedules || schedules.length === 0) return false;

      const normalizeName = (name: string) => {
        if (!name) return "";
        return name.trim().toLowerCase().replace(/\s+\d+$/, "");
      };

      const dayNamesMap: { [key: string]: string } = {
        "Lunes": "Lunes",
        "Martes": "Martes",
        "Miercoles": "Miercoles",
        "Jueves": "Jueves",
        "Viernes": "Viernes",
        "Sabado": "Sabado",
        "Domingo": "Domingo",
      };

      let synced = false;

      Object.keys(rowRefs.current).forEach((positionId) => {
        const rowRef = rowRefs.current[positionId];
        if (!rowRef || !rowRef.syncDayWithSchedule) return;

        const position = positions.find((p: any) => p.id === positionId);
        if (!position) return;

        const positionNameNormalized = normalizeName(position.positionName);

        weekDays.forEach((day) => {
          const dayName = dayNamesMap[day] || day;

          const matchingSchedules = schedules.filter((schedule) => {
            if (!schedule.partner || !schedule.position || !schedule.dayOfWeek) return false;

            const schedulePositionName = normalizeName(schedule.positionName || schedule.position?.name || "");
            const positionMatches = schedulePositionName === positionNameNormalized;
            const shiftMatches =
              normalizeShiftName(schedule.shift) ===
              normalizeShiftName(position.shift);
            const dayMatches = schedule.dayOfWeek === dayName;

            return positionMatches && shiftMatches && dayMatches;
          });

          if (matchingSchedules.length > 0) {
            const schedule = matchingSchedules[0];
            if (rowRef.syncDayWithSchedule(day, schedule)) {
              synced = true;
            }
          }
        });
      });

      return synced;
    },
    exportCurrentTableAsPdf: async (options: { branchName: string; weekName: string; notes?: string }) => {
      const { branchName, weekName, notes } = options;
      const shiftColors: { [key: string]: string } = {
        Matutino: "#F7B917",
        Intermedio: "#1E90FF",
        Vespertino: "#DC2C27",
      };

      const formatTimeForPdf = (schedule: any): string => {
        if (!schedule) return "";
        const startRaw = String(schedule.startTime ?? "").trim();
        const endRaw = String(schedule.endTime ?? "").trim();
        const formatPart = (t: string) => (t.length >= 5 ? t.slice(0, 5) : t);
        const firstOf = (s: string) => {
          if (!s) return "";
          if (s.includes(" - ")) return formatPart(s.split(" - ")[0]?.trim() || s);
          return formatPart(s);
        };
        const lastOf = (s: string) => {
          if (!s) return "";
          if (s.includes(" - ")) {
            const parts = s.split(" - ").map((x: string) => x.trim());
            return formatPart(parts[parts.length - 1] || parts[0] || s);
          }
          return formatPart(s);
        };
        const inicio = firstOf(startRaw);
        const fin = lastOf(endRaw);
        if (inicio && fin) return `${inicio} - ${fin}`;
        return inicio || fin || "";
      };

      const escapeHtmlPdf = (s: string) =>
        String(s ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      const buildPdfAssignmentCardHtml = (
        item: any,
        index: number,
        items: any[],
        shiftRow: string,
      ): string => {
        const posName = escapeHtmlPdf(
          item.positionName || item.position?.name || "",
        );
        const timeStr = formatTimeForPdf(item);
        const timeEsc = escapeHtmlPdf(timeStr);
        const readOnlyCross = isCrossShiftAssignment(item, shiftRow);
        const otherShiftLabel = normalizeShiftName(item.shift);
        const accent = getPositionCardSurfaceStyle(
          item,
          index,
          items,
          shiftRow,
          shiftColors,
        );
        const isFlatPrimary = isPrimarySameShiftPositionCard(
          item,
          index,
          items,
          shiftRow,
        );
        const shellStyle = isFlatPrimary
          ? "padding:2px 4px;box-sizing:border-box;text-align:center;line-height:1.25;"
          : `border-radius:4px;padding:4px 6px;box-sizing:border-box;text-align:center;line-height:1.25;background-color:${accent.backgroundColor};border:${accent.border};border-left:${accent.borderLeft};`;
        const subtitle =
          readOnlyCross && otherShiftLabel
            ? `<div style="font-size:7px;color:#64748B;margin-top:2px;line-height:1.2;">${escapeHtmlPdf(otherShiftLabel)}</div>`
            : "";
        const timeLine = timeEsc
          ? `<div style="font-size:8px;color:#6B7280;margin-top:2px;line-height:1.2;">${timeEsc}</div>`
          : "";
        return `<div style="${shellStyle}"><div style="font-weight:600;font-size:9px;color:#1F2937;">${posName || "—"}</div>${subtitle}${timeLine}</div>`;
      };

      let html = `
        <div style="margin-bottom: 16px;">
          <h1 style="text-align: center; margin: 0 0 8px 0; font-size: 20px; font-weight: 600; color: #1F2937;">Tabla de horario semanal</h1>
          <div style="text-align: center; color: #6B7280; font-size: 13px;">
            <p style="margin: 2px 0;"><strong>Sucursal:</strong> ${branchName}</p>
            <p style="margin: 2px 0;"><strong>Semana:</strong> ${weekName}</p>
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <thead>
            <tr>
              <th style="background-color: #374151; color: #fff; font-weight: 600; padding: 8px 10px; text-align: left; border: 1px solid #4B5563; font-size: 10px;">Colaborador</th>
              <th style="background-color: #374151; color: #fff; font-weight: 600; padding: 8px 10px; text-align: center; border: 1px solid #4B5563; font-size: 10px;">Horario</th>
              ${weekDays.map((d) => `<th style="background-color: #374151; color: #fff; font-weight: 600; padding: 8px 6px; text-align: center; border: 1px solid #4B5563; font-size: 10px;">${d.substring(0, 3)}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
      `;

      shifts.forEach((shift) => {
        html += `
          <tr>
            <td colspan="${2 + weekDays.length}" style="background-color: ${shiftColors[shift]}; color: #fff; font-weight: 700; padding: 6px 10px; text-align: center; font-size: 12px; border: 1px solid ${shiftColors[shift]};">${shift}</td>
          </tr>
        `;
        const partnersInShift = partnersByShift[shift] || [];
        const shiftHoursPdf = getShiftHoursLabel(shift);
        partnersInShift.forEach((partner: any, idx: number) => {
          const rowBg = idx % 2 === 0 ? "#FFFFFF" : "#F9FAFB";
          const partnerName = toCamelCase(
            `${partner.person?.firstName || ""} ${partner.person?.lastName || ""} ${partner.person?.secondLastName || ""}`.trim()
          );

          html += `<tr style="background-color: ${rowBg};"><td style="padding: 5px 8px; border: 1px solid #e5e7eb; font-weight: 600; font-size: 10px;">${partnerName || "-"}</td><td style="padding: 5px 8px; border: 1px solid #e5e7eb; font-size: 10px; text-align: center;">${shiftHoursPdf || "-"}</td>`;

          weekDays.forEach((day) => {
            const cellKey = `${partner.id}-${day}`;
            const cellValue = (cellAssignments[cellKey] ?? "").trim() || "-";
            const isDescanso = cellValue === "DESCANSO";
            const pdfCov = getEssentialDescansoCoverage(
              partner,
              day,
              shift,
              positionsByShift,
              partners,
              cellAssignments,
              positions,
              getSchedulesForPartnerDay,
            );
            const isEssentialUncovered =
              isDescanso && pdfCov.hasEssentialRole && !pdfCov.isFullyCovered;
            const descansoBg = isEssentialUncovered ? "#FEF08A" : "#22C55E";
            const descansoColor = isEssentialUncovered ? "#854D0E" : "#FFFFFF";
            const pdfSubRaw =
              isDescanso && pdfCov.hasEssentialRole
                ? pdfCov.uncovered > 0
                  ? `Cubrir ${pdfCov.required === 1 ? "este puesto esencial" : "estos puestos esenciales"} · ${pdfCov.covered}/${pdfCov.required}`
                  : `Puesto esencial cubierto · ${pdfCov.covered}/${pdfCov.required}`
                : isDescanso
                  ? "Sin conflicto"
                  : "";
            const pdfSubEsc = pdfSubRaw
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;");

            if (isDescanso) {
              html += `<td style="padding: 4px 6px; border: 1px solid #e5e7eb; text-align: center; vertical-align: top; background-color: ${rowBg};"><div style="border-radius:6px;padding:6px 8px;box-sizing:border-box;background-color:${descansoBg};color:${descansoColor};border:1px solid rgba(0,0,0,0.06);line-height:1.25;"><div style="font-size:9px;font-weight:700;">DESCANSO</div>${pdfSubEsc ? `<div style="font-weight:500;font-size:7px;margin-top:4px;">${pdfSubEsc}</div>` : ""}</div></td>`;
            } else if (cellValue && cellValue !== "-") {
              const assignments = getSchedulesForPartnerDay(partner.id, day, shift);
              const hasIndividualDayPdf =
                getSchedulesForPartnerDay(partner.id, day).length > 0;
              const displayItemsPdf = buildScheduleViewDisplayItems(
                partner.id,
                day,
                shift,
                cellValue,
              );
              const items =
                displayItemsPdf.length > 0
                  ? displayItemsPdf
                  : hasIndividualDayPdf
                    ? []
                    : [
                        {
                          positionName: cellValue,
                          position: { name: cellValue },
                          startTime: "",
                          endTime: "",
                        },
                      ];
              const pdfCellBody =
                items.length > 0
                  ? `<div style="display:flex;flex-direction:column;gap:4px;width:100%;align-items:stretch;">${items.map((it: any, idx: number) => buildPdfAssignmentCardHtml(it, idx, items, shift)).join("")}</div>`
                  : hasIndividualDayPdf && assignments.length === 0
                    ? "—"
                    : `<div style="border-radius:4px;padding:4px 6px;box-sizing:border-box;text-align:center;background-color:rgba(107,114,128,0.05);border:1px solid rgba(107,114,128,0.1);border-left:3px solid #cbd5e1;"><div style="font-weight:600;font-size:9px;color:#1F2937;">${escapeHtmlPdf(parseWeeklyCellDropdownOption(cellValue).display)}</div></div>`;
              html += `<td style="padding: 4px 6px; border: 1px solid #e5e7eb; text-align: center; vertical-align: top; font-size: 9px; background-color: ${rowBg};">${pdfCellBody}</td>`;
            } else {
              html += `<td style="padding: 4px 6px; border: 1px solid #e5e7eb; text-align: center; font-size: 10px; color: #9CA3AF;">—</td>`;
            }
          });
          html += "</tr>";
        });
      });

      html += "</tbody></table>";

      if (notes && notes.trim()) {
        const escapedNotes = notes
          .trim()
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/\n/g, "<br/>");
        html += `
        <div style="margin-top: 24px; padding: 16px; background-color: #FEF9C3; border: 1px solid #FDE047; border-radius: 8px;">
          <div style="font-size: 12px; font-weight: 700; color: #854D0E; margin-bottom: 8px;">Notas de la semana</div>
          <div style="font-size: 11px; color: #1F2937; line-height: 1.5;">${escapedNotes}</div>
        </div>
        `;
      }

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.width = "1200px";
      tempDiv.style.backgroundColor = "#ffffff";
      tempDiv.style.padding = "16px";
      tempDiv.style.fontFamily = "Arial, sans-serif";
      tempDiv.innerHTML = html;
      document.body.appendChild(tempDiv);

      try {
        const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
        document.body.removeChild(tempDiv);
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 297;
        const pageHeight = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        const pdf = new jsPDF("landscape", "mm", "a4");
        let position = 0;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        pdf.save(
          `Tabla_Horario_Semanal_${(branchName || "sucursal").replace(/\s+/g, "_")}_${(weekName || "semana").replace(/\s+/g, "_")}_${moment().format("YYYYMMDD_HHmm")}.pdf`
        );
      } catch (e) {
        document.body.removeChild(tempDiv);
        throw e;
      }
    },
    getWeeklySaveContext: () => ({
      cellAssignments,
      initialCellAssignments,
      removedScheduleIds: Array.from(removedScheduleIds),
    }),
  }));

  return (
    <div className="chst-root">
      <div className="chst-scroll">
        <table className="chst-table">
          <thead className="chst-thead">
            <tr>
              {!scheduleViewMode && (
                <th className="chst-th-corner" style={{ minWidth: "170px" }}>
                  <div className="chst-th-corner-pill chst-th-corner-pill--left">
                    Posición
                  </div>
                </th>
              )}
              {scheduleViewMode && (
                <th className="chst-th-corner" style={{ minWidth: "200px" }}>
                  <div className="chst-th-corner-pill">Colaborador</div>
                </th>
              )}
              {scheduleViewMode && (
                <th className="chst-th-corner" style={{ minWidth: "120px" }}>
                  <div className="chst-th-corner-pill">Horario</div>
                </th>
              )}
              {!scheduleViewMode && (
                <th
                  rowSpan={2}
                  className="chst-th-corner"
                  style={{ minWidth: "180px" }}
                >
                  <div className="chst-th-corner-pill">Colaborador</div>
                </th>
              )}
              {weekDays.map((day, index) => {
                const dayDate = weekValue
                  ? getDateByDayOfWeek(weekValue, day)
                  : null;
                const dayNumber = dayDate ? dayDate.getDate() : null;
                return (
                <th
                  key={index}
                  className={`chst-day-header${onDayClick ? " chst-day-header--link" : ""}`}
                  onClick={() => onDayClick?.(day)}
                  title={onDayClick ? `Ir al horario del ${day}` : undefined}
                  style={{
                    minWidth: "100px",
                    cursor: onDayClick ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  <span className="chst-day-header-pill">
                    {dayNumber != null ? (
                      <span className="chst-day-header-date">{dayNumber}</span>
                    ) : null}
                    <span className="chst-day-header-name">
                      {day.substring(0, 3).toUpperCase()}
                    </span>
                  </span>
                </th>
              );
              })}
            </tr>
          </thead>
          <tbody>
            {scheduleViewMode ? (
              shifts.map((shift, shiftIdx) => (
                <React.Fragment key={shift}>
                  <tr className="chst-shift-banner" style={{ animationDelay: `${shiftIdx * 70}ms` }}>
                    <td colSpan={2 + weekDays.length} className="chst-shift-banner-cell">
                      <div
                        className="chst-shift-pill"
                        style={{ backgroundColor: shiftColors[shift] }}
                      >
                        {shift}
                      </div>
                    </td>
                  </tr>
                  {(partnersByShift[shift] || []).map((partner: any, idx: number) => {
                    const partnerName = toCamelCase(
                      `${partner.person?.firstName || ""} ${partner.person?.lastName || ""} ${partner.person?.secondLastName || ""}`.trim()
                    );
                    const primaryShift = (partner.candidate?.shift?.name || "").trim();
                    const isInOtherShift = primaryShift !== shift;
                    const basePositionName =
                      partner.candidate?.vacancy?.position?.name || "";
                    const positionInShift = getPositionNameForPartnerInShift(partner.id, shift);
                    const displayPosition = isInOtherShift
                      ? (positionInShift || basePositionName || "-")
                      : (basePositionName || "-");
                    const hasMultiplePositions = getPartnerRowCount(partner.id, shift) > 1;
                    const shiftHours = getShiftHoursLabel(shift);
                    return (
                      <tr
                        key={partner.id}
                        className="chst-partner-row"
                        style={{
                          animationDelay: `${Math.min(idx, 10) * 30}ms`,
                        }}
                      >
                        <td>
                          <div className="chst-partner-card">
                            <BossProfileAvatar
                              partnerId={partner.id}
                              curp={partner.person?.curp}
                              firstName={partner.person?.firstName}
                              lastName={partner.person?.lastName}
                              size={38}
                            />
                            <div className="chst-partner-card__info">
                              <div className="chst-partner-card__name">
                                {partnerName || "-"}
                              </div>
                              <div className="chst-partner-card__position">
                                {displayPosition}
                                {hasMultiplePositions ? (
                                  <span className="chst-partner-card__meta">
                                    (varios puestos)
                                  </span>
                                ) : null}
                                {isInOtherShift && displayPosition ? (
                                  <span className="chst-partner-card__meta">
                                    (doble turno)
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            className="chst-hours-card"
                            style={{ color: shiftHours ? "#374151" : "#9CA3AF" }}
                          >
                            {shiftHours || "-"}
                          </div>
                        </td>
                        {weekDays.map((day) => {
                          const cellKey = `${partner.id}-${day}`;
                          const assignments = getSchedulesForPartnerDay(partner.id, day, shift);
                          const cellValue = cellAssignments[cellKey] ?? "";
                          const isDescanso = cellValue === "DESCANSO";
                          const initialValue = initialCellAssignments[cellKey] ?? "";
                          const isEmpty = (v: string) =>
                            !(v ?? "").trim() || (v ?? "").trim() === "-";
                          const isNewAssignment =
                            isEmpty(initialValue) && !isEmpty(cellValue);
                          const isPendingDeletion =
                            !isEmpty(initialValue) && isEmpty(cellValue);
                          const isOpen =
                            scheduleCellEditing?.partnerId === partner.id &&
                            scheduleCellEditing?.day === day &&
                            scheduleCellEditing?.shift === shift;
                          const hasIndividualDay =
                            hasIndividualScheduleForPartnerDay(partner.id, day);
                          const displayItems = buildScheduleViewDisplayItems(
                            partner.id,
                            day,
                            shift,
                            cellValue,
                          );
                          const hasVariosPuestos = displayItems.length >= 2;
                          const sameShiftDisplayCount = displayItems.filter(
                            (d: any) => !isCrossShiftAssignment(d, shift),
                          ).length;
                          const canAddSecondPosition =
                            !isDescanso &&
                            displayItems.length < 2 &&
                            (sameShiftDisplayCount === 1 ||
                              (sameShiftDisplayCount === 0 &&
                                displayItems.length === 1));
                          const isAddDropzoneOpen =
                            isOpen && scheduleCellEditing?.mode === "add";
                          const cellHoverKey = `${partner.id}-${day}-${shift}`;
                          const isCellHovered = hoveredScheduleCellKey === cellHoverKey;
                          const isAddSlotVisible =
                            isAddDropzoneOpen ||
                            addSlotVisibleCellKey === cellHoverKey;
                          const partnersInShiftList = partnersByShift[shift] || [];
                          const essCov = getEssentialDescansoCoverage(
                            partner,
                            day,
                            shift,
                            positionsByShift,
                            partners,
                            cellAssignments,
                            positions,
                            getSchedulesForPartnerDay,
                          );
                          const isEssentialUncovered =
                            isDescanso && essCov.hasEssentialRole && !essCov.isFullyCovered;
                          const descansoBg = isEssentialUncovered ? "#FEF08A" : "#22C55E";
                          const coverList = essCov.coverNames.join(", ");
                          const descansoTitle = isDescanso
                            ? essCov.hasEssentialRole
                              ? essCov.uncovered > 0
                                ? `Descanso: cubrir puesto esencial (${essCov.covered}/${essCov.required})${coverList ? `. Cubren: ${coverList}` : ""}`
                                : `Descanso: puesto esencial cubierto (${essCov.covered}/${essCov.required})${coverList ? `. Cubren: ${coverList}` : ""}`
                              : "Descanso: sin conflicto"
                            : undefined;
                          const descansoNeedsEssentialCover =
                            isDescanso &&
                            essCov.hasEssentialRole &&
                            essCov.uncovered > 0;
                          const descansoHasEssentialCover =
                            isDescanso &&
                            essCov.hasEssentialRole &&
                            essCov.uncovered === 0;
                          const descansoNoEssentialConflict =
                            isDescanso && !essCov.hasEssentialRole;
                          const hasUniformSingleCell =
                            !isDescanso && !hasVariosPuestos;
                          const cellSurfaceClass = [
                            "chst-cell-surface",
                            isDescanso ? "chst-cell-surface--descanso" : "",
                            hasVariosPuestos ? "chst-cell-surface--multi" : "",
                            hasUniformSingleCell
                              ? "chst-cell-surface--single"
                              : "",
                            canAddSecondPosition && isAddSlotVisible
                              ? "chst-cell-surface--slot-open"
                              : "",
                            !isDescanso && isPendingDeletion
                              ? "chst-cell-surface--pending-delete"
                              : "",
                            !isDescanso && isNewAssignment
                              ? "chst-cell-surface--new"
                              : "",
                            !isDescanso &&
                            displayItems.length === 0 &&
                            !(hasIndividualDay && assignments.length === 0)
                              ? "chst-cell-surface--empty"
                              : "",
                            canAddSecondPosition ? "chst-cell-surface--expandable" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td
                              key={day}
                              className={`chst-schedule-cell${
                                isOpen ? " chst-schedule-cell--open" : ""
                              }${
                                isCellHovered && !isDescanso && !isOpen
                                  ? " chst-schedule-cell--hovered"
                                  : ""
                              }`}
                              style={{
                                cursor: isDescanso ? "default" : "pointer",
                              }}
                              title={
                                isDescanso
                                  ? descansoTitle
                                  : isPendingDeletion
                                    ? "Se eliminará del horario al guardar"
                                    : "Clic en la tarjeta para reemplazar ese puesto"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isDescanso) return;
                                setScheduleCellEditing({
                                  partnerId: partner.id,
                                  day,
                                  shift,
                                  mode: "replace",
                                  scheduleId: null,
                                  replaceTargetLabel: null,
                                });
                                setCellFilterText("");
                              }}
                              onMouseEnter={() => {
                                if (!isDescanso) {
                                  setHoveredScheduleCellKey(cellHoverKey);
                                  clearAddSlotHoverTimer();
                                  setAddSlotVisibleCellKey((prev) =>
                                    prev === cellHoverKey ? prev : null,
                                  );
                                  if (canAddSecondPosition && !isAddDropzoneOpen) {
                                    scheduleAddSlotReveal(cellHoverKey);
                                  }
                                }
                              }}
                              onMouseLeave={() => {
                                clearAddSlotHoverTimer();
                                if (!isAddDropzoneOpen) {
                                  setHoveredScheduleCellKey((prev) =>
                                    prev === cellHoverKey ? null : prev,
                                  );
                                  setAddSlotVisibleCellKey((prev) =>
                                    prev === cellHoverKey ? null : prev,
                                  );
                                }
                              }}
                            >
                              <div
                                className={cellSurfaceClass}
                                style={{
                                  backgroundColor: isDescanso ? descansoBg : undefined,
                                  color: isDescanso
                                    ? isEssentialUncovered
                                      ? "#854D0E"
                                      : "#FFFFFF"
                                    : displayItems.length
                                    ? "#1F2937"
                                    : isPendingDeletion
                                    ? "#991B1B"
                                    : "#9CA3AF",
                                  fontWeight: isDescanso ? 600 : 400,
                                  fontSize: "10px",
                                  textAlign: "center",
                                }}
                              >
                              <div className="chst-cell-content">
                                {isDescanso ? (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                    <span>DESCANSO</span>
                                    {descansoNeedsEssentialCover ? (
                                      <>
                                        <span
                                          style={{
                                            fontSize: "8px",
                                            opacity: 0.95,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                            wordBreak: "break-word",
                                          }}
                                          title={descansoTitle}
                                        >
                                          {essCov.required === 1
                                            ? "Cubrir este puesto esencial"
                                            : "Cubrir estos puestos esenciales"}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "9px",
                                            fontWeight: 700,
                                            lineHeight: 1.1,
                                          }}
                                          title={descansoTitle}
                                        >
                                          {essCov.covered}/{essCov.required}
                                        </span>
                                      </>
                                    ) : descansoHasEssentialCover ? (
                                      <>
                                        <span
                                          style={{
                                            fontSize: "8px",
                                            opacity: 0.95,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                          }}
                                          title={descansoTitle}
                                        >
                                          Puesto esencial cubierto
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "9px",
                                            fontWeight: 700,
                                            lineHeight: 1.1,
                                          }}
                                          title={descansoTitle}
                                        >
                                          {essCov.covered}/{essCov.required}
                                        </span>
                                      </>
                                    ) : descansoNoEssentialConflict ? (
                                      <span
                                        style={{
                                          fontSize: "8px",
                                          opacity: 0.95,
                                          lineHeight: 1.2,
                                          textAlign: "center",
                                          wordBreak: "break-word",
                                        }}
                                        title={descansoTitle}
                                      >
                                        Sin conflicto
                                      </span>
                                    ) : null}
                                  </div>
                                ) : displayItems.length > 0 ? (
                                  (() => {
                                    const singleFunction = displayItems.length === 1;
                                    return displayItems.map((item: any, i: number) => {
                                      const posName = item.positionName || item.position?.name || "";
                                      const readOnlyCross = isCrossShiftAssignment(
                                        item,
                                        shift,
                                      );
                                      const cardSurfaceStyle =
                                        getPositionCardSurfaceStyle(
                                          item,
                                          i,
                                          displayItems,
                                          shift,
                                          shiftColors,
                                        );
                                      const isFlatPrimaryCard =
                                        isPrimarySameShiftPositionCard(
                                          item,
                                          i,
                                          displayItems,
                                          shift,
                                        );
                                      const timeStr =
                                        formatScheduleTimeRange(item) ||
                                        (singleFunction && !readOnlyCross ? shiftHours : "");
                                      const isSingleCard = singleFunction;
                                      const cardHoverKey = `${cellKey}-${i}`;
                                      return (
                                        <div
                                          key={i}
                                          title={
                                            readOnlyCross
                                              ? "Asignación en otro turno. Clic para reemplazar por un puesto de este turno."
                                              : "Clic para reemplazar este puesto"
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (readOnlyCross) {
                                              setScheduleCellEditing({
                                                partnerId: partner.id,
                                                day,
                                                shift,
                                                mode: "replace",
                                                scheduleId:
                                                  Number(
                                                    item?.id ?? item?.branchScheduleId,
                                                  ) || null,
                                                replaceTargetLabel:
                                                  item._cellPartLabel ||
                                                  displayItemToCellPart(item, shift),
                                              });
                                              setCellFilterText("");
                                              return;
                                            }
                                            setScheduleCellEditing({
                                              partnerId: partner.id,
                                              day,
                                              shift,
                                              mode: "replace",
                                              scheduleId:
                                                Number(item?.id ?? item?.branchScheduleId) ||
                                                null,
                                              replaceTargetLabel:
                                                item._cellPartLabel ||
                                                displayItemToCellPart(item, shift),
                                            });
                                            setCellFilterText("");
                                          }}
                                          onMouseEnter={() => setHoveredCardKey(cardHoverKey)}
                                          onMouseLeave={() => setHoveredCardKey((prev) => (prev === cardHoverKey ? null : prev))}
                                          className={`chst-position-card${
                                            isSingleCard
                                              ? " chst-position-card--single"
                                              : " chst-position-card--multi"
                                          }${
                                            isFlatPrimaryCard
                                              ? " chst-position-card--flat"
                                              : ""
                                          }`}
                                          style={{
                                            lineHeight: 1.3,
                                            padding: "6px 8px",
                                            backgroundColor:
                                              cardSurfaceStyle.backgroundColor,
                                            border: cardSurfaceStyle.border,
                                            borderLeft:
                                              cardSurfaceStyle.borderLeft,
                                            width: "100%",
                                            boxSizing: "border-box",
                                            position: "relative",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <button
                                            type="button"
                                            title={
                                              readOnlyCross
                                                ? "Quitar asignación del otro turno"
                                                : "Quitar asignación"
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const scheduleId = Number(
                                                item?.id ?? item?.branchScheduleId,
                                              );
                                              const idsToRemove = displayItems
                                                .map((di: any) =>
                                                  Number(di?.id ?? di?.branchScheduleId),
                                                )
                                                .filter(
                                                  (id: number) =>
                                                    !Number.isNaN(id) && id > 0,
                                                );
                                              const partLabel = String(
                                                item._cellPartLabel ||
                                                  displayItemToCellPart(item, shift),
                                              ).trim();
                                              const nextCellValue = partLabel
                                                ? replaceOneWeeklyCellPart(
                                                    cellValue,
                                                    partLabel,
                                                    "",
                                                  )
                                                : "";
                                              setRemovedScheduleIds((prev) => {
                                                const next = new Set(prev);
                                                if (
                                                  !Number.isNaN(scheduleId) &&
                                                  scheduleId > 0
                                                ) {
                                                  next.add(scheduleId);
                                                }
                                                if (!nextCellValue) {
                                                  idsToRemove.forEach((id) =>
                                                    next.add(id),
                                                  );
                                                }
                                                return next;
                                              });
                                              setCellAssignments((prev) => ({
                                                ...prev,
                                                [cellKey]: nextCellValue,
                                              }));
                                              setScheduleCellEditing(null);
                                              setCellFilterText("");
                                            }}
                                            style={{
                                              position: "absolute",
                                              top: "3px",
                                              right: "4px",
                                              border: hoveredCardKey === cardHoverKey ? "1px solid #DC2626" : "1px solid transparent",
                                              borderRadius: "9999px",
                                              background: hoveredCardKey === cardHoverKey ? "#EF4444" : "transparent",
                                              color: hoveredCardKey === cardHoverKey ? "#FFFFFF" : "transparent",
                                              cursor: hoveredCardKey === cardHoverKey ? "pointer" : "default",
                                              width: "18px",
                                              height: "18px",
                                              lineHeight: 1,
                                              fontSize: "14px",
                                              fontWeight: 700,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              opacity: hoveredCardKey === cardHoverKey ? 1 : 0,
                                              pointerEvents: hoveredCardKey === cardHoverKey ? "auto" : "none",
                                              transition: "all 0.12s ease",
                                            }}
                                          >
                                            ×
                                          </button>
                                          <div
                                            className={
                                              isSingleCard
                                                ? "chst-position-card__title"
                                                : undefined
                                            }
                                            style={
                                              isSingleCard
                                                ? undefined
                                                : {
                                                    fontWeight: 600,
                                                    color: "#1F2937",
                                                    fontSize: "10px",
                                                  }
                                            }
                                          >
                                            {posName || "-"}
                                          </div>
                                          {readOnlyCross ? (
                                            <div style={{ fontSize: "8px", color: "#64748B", marginTop: "2px" }}>
                                              Otro turno
                                            </div>
                                          ) : null}
                                          {timeStr ? (
                                            <div style={{ fontSize: "9px", color: "#6B7280", marginTop: "2px" }}>
                                              {timeStr}
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    });
                                  })()
                                ) : (
                                  <>
                                    {hasIndividualDay &&
                                    assignments.length === 0 &&
                                    displayItems.length === 0 ? (
                                      <span className="chst-empty-dash">—</span>
                                    ) : (
                                      (() => {
                                        const cv = (cellValue || "").trim();
                                        if (!cv || cv === "-") {
                                          return (
                                            <span className="chst-empty-dash">-</span>
                                          );
                                        }
                                        return parseWeeklyCellDropdownOption(cv).display;
                                      })()
                                    )}
                                  </>
                                )}
                              </div>
                              {canAddSecondPosition ? (
                                <div
                                  className={`chst-add-slot-wrap${
                                    isAddSlotVisible
                                      ? " chst-add-slot-wrap--visible"
                                      : ""
                                  }`}
                                >
                                  <div className="chst-add-slot-wrap-inner">
                                    <div
                                      role="button"
                                      tabIndex={isAddSlotVisible ? 0 : -1}
                                      aria-hidden={!isAddSlotVisible}
                                      className={`chst-add-slot${
                                        isAddDropzoneOpen
                                          ? " chst-add-slot--open"
                                          : ""
                                      }`}
                                      title="Agrega un segundo puesto en este turno (no reemplaza el actual)"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setScheduleCellEditing({
                                          partnerId: partner.id,
                                          day,
                                          shift,
                                          mode: "add",
                                          scheduleId: null,
                                          replaceTargetLabel: null,
                                        });
                                        setCellFilterText("");
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setScheduleCellEditing({
                                            partnerId: partner.id,
                                            day,
                                            shift,
                                            mode: "add",
                                            scheduleId: null,
                                            replaceTargetLabel: null,
                                          });
                                          setCellFilterText("");
                                        }
                                      }}
                                    >
                                      <span className="chst-add-slot-plus">+</span>
                                      <span className="chst-add-slot-label">
                                        Agrega 2do puesto
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              </div>
                              {isOpen && !isDescanso ? (
                                <div
                                  ref={scheduleDropdownRef}
                                  className="chst-dropdown"
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: "100%",
                                    marginTop: "2px",
                                    minWidth: "160px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #D1D5DB",
                                    borderRadius: "6px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    overflow: "hidden",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div
                                    style={{
                                      padding: "5px 8px",
                                      fontSize: "9px",
                                      fontWeight: 600,
                                      color: "#6B7280",
                                      backgroundColor: "#F9FAFB",
                                      borderBottom: "1px solid #e5e7eb",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.4px",
                                    }}
                                  >
                                    {scheduleCellEditing?.mode === "add"
                                      ? "Agrega 2do puesto"
                                      : "Reemplazar puesto"}
                                  </div>
                                  <input
                                    type="text"
                                    value={cellFilterText}
                                    onChange={(e) => setCellFilterText(e.target.value)}
                                    placeholder="Buscar puesto..."
                                    autoFocus
                                    style={{
                                      width: "100%",
                                      padding: "6px 8px",
                                      fontSize: "11px",
                                      border: "none",
                                      borderBottom: "1px solid #e5e7eb",
                                      outline: "none",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                  <div
                                    style={{
                                      maxHeight: "180px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {filteredCellOptions.map((opt) => {
                                      const isSelected = (cellValue || "-") === opt;
                                      const parsedOpt = parseWeeklyCellDropdownOption(opt);
                                      const optDisplay = parsedOpt.display;
                                      const isEssential =
                                        essentialPositionDisplayNames.has(opt) ||
                                        essentialPositionDisplayNames.has(optDisplay);
                                      const optShiftForBorder =
                                        opt === "DESCANSO"
                                          ? null
                                          : resolveShiftForSlotFilterOption(
                                              optDisplay,
                                              parsedOpt.explicitShift,
                                              shift,
                                              positionGroupByShift,
                                              SCHEDULE_VIEW_SHIFT_ORDER,
                                            ) || shift;
                                      const optBorderLeft =
                                        opt === "DESCANSO"
                                          ? "3px solid #cbd5e1"
                                          : optShiftForBorder &&
                                              shiftColors[optShiftForBorder]
                                            ? `4px solid ${shiftColors[optShiftForBorder]}`
                                            : "3px solid #94a3b8";
                                      const baseBg = isSelected
                                        ? "#EFF6FF"
                                        : isEssential
                                        ? "#FEF9C3"
                                        : optShiftForBorder
                                        ? optShiftForBorder === shift
                                          ? "rgba(107, 114, 128, 0.04)"
                                          : "rgba(148, 163, 184, 0.08)"
                                        : "transparent";
                                      return (
                                        <div
                                          key={opt}
                                          onClick={() => {
                                            applyScheduleCellOption(opt, {
                                              cellKey,
                                              cellValue,
                                              mode:
                                                scheduleCellEditing?.mode ??
                                                "replace",
                                              shift,
                                              displayItems,
                                              replaceScheduleId:
                                                scheduleCellEditing?.scheduleId,
                                              replaceTargetLabel:
                                                scheduleCellEditing?.replaceTargetLabel,
                                            });
                                            setScheduleCellEditing(null);
                                            setCellFilterText("");
                                          }}
                                          style={{
                                            padding: "6px 8px",
                                            paddingLeft: "10px",
                                            fontSize: "11px",
                                            cursor: "pointer",
                                            backgroundColor: baseBg,
                                            color: opt === "DESCANSO" ? "#F97316" : "#1F2937",
                                            borderLeft: optBorderLeft,
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isSelected)
                                              e.currentTarget.style.backgroundColor = isEssential ? "#FEF08A" : "#F3F4F6";
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isSelected)
                                              e.currentTarget.style.backgroundColor = isEssential
                                                ? "#FEF9C3"
                                                : optShiftForBorder
                                                  ? optShiftForBorder === shift
                                                    ? "rgba(107, 114, 128, 0.04)"
                                                    : "rgba(148, 163, 184, 0.08)"
                                                  : "transparent";
                                          }}
                                        >
                                          {optDisplay}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              shifts.map((shift, shiftIdx) => (
                <React.Fragment key={shift}>
                  <tr className="chst-shift-banner" style={{ animationDelay: `${shiftIdx * 70}ms` }}>
                    <td colSpan={weekDays.length + 2} className="chst-shift-banner-cell">
                      <div
                        className="chst-shift-pill"
                        style={{ backgroundColor: shiftColors[shift] }}
                      >
                        {shift}
                      </div>
                    </td>
                  </tr>
                  {positionsByShift[shift]?.map((position, index) => {
                    const assignedPartner = getPartnerFromSchedule(position, shift);
                    const matchedPartner = getPartnerForPosition(position);

                    return (
                      <ConfigHorarioSemanalRow
                        key={position.id}
                        ref={(el) => {
                          if (el) {
                            rowRefs.current[position.id] = el;
                          } else {
                            delete rowRefs.current[position.id];
                          }
                        }}
                        position={position}
                        assignedPartner={assignedPartner}
                        matchedPartner={matchedPartner}
                        weekDays={weekDays}
                        weekValue={weekValue}
                        schedules={schedules}
                        partners={partners}
                        shift={shift}
                        shiftBgColor={shiftBgColors[shift]}
                        availableRoles={availableRoles}
                        editingCell={editingCell}
                        setEditingCell={setEditingCell}
                        rowIndex={index}
                        branchId={branchId}
                        freeDays={freeDays}
                        weekendSchedule={weekendSchedule}
                        scheduleViewMode={scheduleViewMode}
                        getScheduleItemForCell={getScheduleItemForCell}
                        getScheduleItemsForCell={getScheduleItemsForCell}
                        onDayClick={onDayClick}
                      />
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ConfigHorarioSemanalTable;
